import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    FormGroup,
    FormControlLabel,
    Checkbox,
    FormLabel,
    RadioGroup,
    Radio,
    Tabs,
    Tab,
    Snackbar,
    Chip,
    Avatar,
    Badge,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    School as EducationIcon,
    Work as WorkIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Description as FileIcon,
    AccessTime as TimeIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import TestingLaboratoryService from '../../../services/testinglaboratoryService';
import UnfreezeApiService from '../../../services/UnfreezeApiService';
import FileUploadComponent from '../../../components/FileUploadComponent';
import {
    insertDANC,
    updateDANCBySourceId,
    getDANC,
} from '../../../services/apiCalibration';
import {
    getScrutinyRemarks,
    createScrutinyRemark,
    getClauseNo
} from '../../../services/api';
import s3Utils from '../../../utils/s3Utils';
import useAssessmentTracking from "../../../hooks/useAssessmentTracking";
import {
  LocationWarningDialog,
  HourlyAttendanceDialog,
} from "../../../components/AssessmentTrackingDialogs";

const LabStaffSection = () => {
    const { orgId: urlOrgId } = useParams();
    const location = useLocation();
    const [assessorDisciplines, setAssessorDisciplines] = useState([]);

    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 LabStaffSection - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 LabStaffSection - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ LabStaffSection - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get assessmentId from URL query parameters with decryption
    const getAssessmentIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessmentId = searchParams.get("assessmentId");
        if (encryptedAssessmentId) {
            try {
                const decryptedAssessmentId = decryptText(encryptedAssessmentId);
                console.log(
                    "📍 Using decrypted assessmentId from URL query:",
                    decryptedAssessmentId
                );
                return decryptedAssessmentId;
            } catch (error) {
                return encryptedAssessmentId;
            }
        }
        return 0;
    };

    // Function to get labType from URL query parameters with decryption
    const getLabTypeFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedLabType = searchParams.get("labType");
        if (encryptedLabType) {
            try {
                const decryptedLabType = decryptText(encryptedLabType);
                console.log(
                    "📍 Using decrypted labType from URL query:",
                    decryptedLabType
                );
                return decryptedLabType;
            } catch (error) {
                return encryptedLabType;
            }
        }
        return null;
    };

    // Function to get assessorId from URL query parameters with decryption
    const getAssessorIdFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAssessorId = searchParams.get("assessorId");
        if (encryptedAssessorId) {
            try {
                const decryptedAssessorId = decryptText(encryptedAssessorId);
                console.log(
                    "📍 Using decrypted assessorId from URL query:",
                    decryptedAssessorId
                );
                return decryptedAssessorId;
            } catch (error) {
                return encryptedAssessorId;
            }
        }
        return 0;
    };

    const getCapacityFromQuery = () => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedCapacity = searchParams.get("capacity");
        if (encryptedCapacity) {
            try {
                const decryptedCapacity = decryptText(encryptedCapacity);
                console.log(
                    "📍 Using decrypted capacity from URL query:",
                    decryptedCapacity
                );
                return decryptedCapacity;
            } catch (error) {
                return encryptedCapacity;
            }
        }
        return null;
    };

    const getAssessmentId = () => {
        const queryAssessmentId = getAssessmentIdFromQuery();
        if (queryAssessmentId) {
            return queryAssessmentId;
        }
        return 0;
    };

    const getLabType = () => {
        const queryLabType = getLabTypeFromQuery();
        if (queryLabType) {
            return queryLabType;
        }
        return null;
    };

    const getAssessorId = () => {
        const queryAssessorId = getAssessorIdFromQuery();
        if (queryAssessorId) {
            return queryAssessorId;
        }
        return 0;
    };


    const getCapacity = () => {
        const queryCapacity = getCapacityFromQuery();
        if (queryCapacity) {
            return queryCapacity;
        }
        return null;
    };

    const userTypeId = localStorage.getItem('userTypeId');

    // Get assessmentId and assessorId for userType 6 (Assessment)
    const assessmentId = getAssessmentId();
    const assessorId = getAssessorId();

    // Function to get orgId with priority: URL query (encrypted) > URL params > null
    const getOrgId = useCallback(() => {
        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 LabStaffSection - Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 LabStaffSection - Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ LabStaffSection - No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: URL query (encrypted) only
    const getAppId = useCallback(() => {
        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 LabStaffSection - Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ LabStaffSection - No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    const [orgId, setOrgId] = useState(() => {
        // Initialize with query parameters first, then URL params
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('🚀 LabStaffSection - Initial orgId from encrypted query:', decryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('Failed to decrypt initial orgId, using original:', encryptedOrgId);
                return encryptedOrgId;
            }
        }
        // Only fallback to URL params, no localStorage
        return urlOrgId || null;
    });

    const [appId, setAppId] = useState(() => {
        // Initialize with query parameters only
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('🚀 LabStaffSection - Initial appId from encrypted query:', decryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('Failed to decrypt initial appId, using original:', encryptedAppId);
                return encryptedAppId;
            }
        }
        // No fallback - must be provided via query parameters
        return null;
    });

    // Update orgId state only (no localStorage)
    const updateOrgId = (newOrgId) => {
        console.log('Updating orgId state:', newOrgId);
        setOrgId(newOrgId);
    };

    // Update appId state only (no localStorage)
    const updateAppId = (newAppId) => {
        console.log('Updating appId state:', newAppId);
        setAppId(newAppId);
    };

     // Assessment tracking hook for location and hourly attendance
      const {
        locationWarningOpen,
        distanceMessage,
        attendanceCheckDialogOpen,
        attendanceCheckMessage,
        setLocationWarningOpen,
        setAttendanceCheckDialogOpen,
        checkHourlyAttendance,
      } = useAssessmentTracking({
        assessorId,
        appId,
        orgId,
      });

    // useEffect to handle parameter updates from URL
    useEffect(() => {
        const currentOrgId = getOrgId();
        if (currentOrgId !== orgId) {
            updateOrgId(currentOrgId);
        }

        const currentAppId = getAppId();
        if (currentAppId !== appId) {
            console.log('LabStaffSection - AppId from URL query params:', currentAppId);
            updateAppId(currentAppId);
        }
    }, [urlOrgId, location.search, orgId, appId, getOrgId, getAppId]);

    // Initialize to show "View Staff" (Existing) tab by default
    // When stage is null/""/"18"/"18a"/"18b", we have 2 tabs: 0=Add/Edit Staff, 1=View Staff
    // When stage is other, we have 1 tab: 0=View Staff
    // Default to showing "View Staff" tab which is index 1 when both tabs are visible
    const [tabValue, setTabValue] = useState(1);
    const [staffList, setStaffList] = useState([]);
    const [editingStaff, setEditingStaff] = useState(null);
    const [employeeType, setEmployeeType] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedDisciplines, setSelectedDisciplines] = useState([]);
    const [disciplines, setDisciplines] = useState([]);
    const [disciplinesLoading, setDisciplinesLoading] = useState(false);
    const [error, setError] = useState('');

    // Scrutiny Remarks states
    const [scrutinyRemarks, setScrutinyRemarks] = useState([]);
    const [scrutinyRemarksLoading, setScrutinyRemarksLoading] = useState(false);
    const [isDropdownSelection, setIsDropdownSelection] = useState(false);

    // Clause Number states
    const [clauseNo, setClauseNo] = useState([]);

    // Bulk Adequacy Check States
    const [bulkAdequacyModalOpen, setBulkAdequacyModalOpen] = useState(false);
    const [selectedAdequacyStaff, setSelectedAdequacyStaff] = useState([]);
    const [bulkAdequacyStatus, setBulkAdequacyStatus] = useState("");
    const [bulkAdequacyRemarks, setBulkAdequacyRemarks] = useState("");
    const [bulkAdequacyLoading, setBulkAdequacyLoading] = useState(false);

    // State for bulk adequacy multiple clause selection
    const [selectedClauses, setSelectedClauses] = useState([]);
    
    // State for clause search filter
    const [clauseSearchFilter, setClauseSearchFilter] = useState("");
    
    // Ref for clause search input
    const clauseSearchInputRef = useRef(null);

    // Bulk Adequacy Scrutiny Remarks States
    const [bulkScrutinyRemarks, setBulkScrutinyRemarks] = useState([]);
    const [bulkLoadingRemarks, setBulkLoadingRemarks] = useState(false);

    // Checkbox and adequacy states
    const [selectedItems, setSelectedItems] = useState([]);
    const [adequacyDialogOpen, setAdequacyDialogOpen] = useState(false);
    const [adequacyStatus, setAdequacyStatus] = useState('');
    const [adequacyRemarks, setAdequacyRemarks] = useState('');
    const [selectedDisciplineId, setSelectedDisciplineId] = useState(''); // For discipline dropdown in adequacy dialog
    const [orgStatusRecords, setOrgStatusRecords] = useState([]); // Store multiple org status records for current staff
    const [staffOrgStatuses, setStaffOrgStatuses] = useState({}); // Store all org statuses for all staff {staffId: [{...}, {...}]}
    const [applicationStage, setApplicationStage] = useState(null); // New state for application stage
    
    // New states for discipline-wise adequacy dialog (separate from checkbox dialog)
    const [disciplineDialogOpen, setDisciplineDialogOpen] = useState(false);
    const [selectedStaffForDiscipline, setSelectedStaffForDiscipline] = useState(null);
    const [disciplineAdequacyStatus, setDisciplineAdequacyStatus] = useState('');
    const [disciplineAdequacyRemarks, setDisciplineAdequacyRemarks] = useState('');
    const [selectedDisciplineForRow, setSelectedDisciplineForRow] = useState('');
    const [selectedGroupForRow, setSelectedGroupForRow] = useState('');
    const [disciplineOrgStatusRecords, setDisciplineOrgStatusRecords] = useState([]);
    const [selectedDisciplineClauses, setSelectedDisciplineClauses] = useState([]);
    const [disciplineClauseSearchFilter, setDisciplineClauseSearchFilter] = useState('');
    const disciplineClauseSearchInputRef = useRef(null);

    // DANC Integration States
    const [dancRecords, setDancRecords] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [adequacyChecks, setAdequacyChecks] = useState({});

    // Unfreeze records state
    const [unfreezeRecords, setUnfreezeRecords] = useState([]);

    // Current user for DANC system - get from auth context or session
    const [currentUser] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('currentUser')) || 'Admin';
        } catch {
            return 'Admin';
        }
    });

    // Current user ID for DANC system - get numeric ID for Insertedby field
    const [currentUserId] = useState(() => {
        try {
            return parseInt(localStorage.getItem('currentUserId')) || 1;
        } catch {
            return 1;
        }
    });

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' // 'success', 'error', 'warning', 'info'
    });

    // Form fields state
    const [formData, setFormData] = useState({
        salutation: '',
        name: '',
        designation: '',
        qualification: '',
        specialization: '',
        experience: '',
        training: '',
        landlineNo: '',
        mobileNo: '',
        email: '',
        reportReview: '',
        partFullTime: '',
        timing: ''
    });

    // File state - store file paths and names for database
    const [fileData, setFileData] = useState({
        specimenSignature: {
            fileName: '',
            filePath: ''
        }
    });

    // Snackbar helper function
    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const fetchDisciplines = useCallback(async () => {
        setDisciplinesLoading(true);
        setError('');
        try {
            console.log('🔄 LabStaffSection - Fetching disciplines with appId:', appId, 'orgId:', orgId);
            const response = await TestingLaboratoryService.getSelectedDisciplines(appId, orgId);
            if (response.success) {
                setDisciplines(response.data);
            } else {
                setError('Failed to fetch disciplines');
            }
        } catch (error) {
            setError(error.message || 'Failed to fetch disciplines');
        } finally {
            setDisciplinesLoading(false);
        }
    }, [appId, orgId]);

    // Fetch organization status for staff adequacy
    const fetchOrgStatus = useCallback(async (empId) => {
        try {
            const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getorgstatus?appid=${appId}&assessmentid=${assessmentId || ''}&statusby=${assessorId}`;
            
            console.log('📥 Fetching org status from:', url);
            console.log('🔍 Looking for emp_id:', empId);
            
            const response = await axios.get(url);
            const data = response.data;
            
            console.log('📥 Org status response:', data);
            console.log('📥 Response type:', typeof data);
            console.log('📥 Is Array:', Array.isArray(data));
            
            // Handle different response structures
            let statusArray = data;
            
            // If response has a nested structure like { org: [...] }
            if (data && data.org && Array.isArray(data.org)) {
                statusArray = data.org;
                console.log('📦 Using data.org array');
            } 
            // If response is directly an array
            else if (Array.isArray(data)) {
                statusArray = data;
                console.log('📦 Using data array directly');
            }
            // If response has other nested structure
            else if (data && typeof data === 'object') {
                console.log('📦 Response is object, checking properties:', Object.keys(data));
                // Try to find array in response
                const arrayKey = Object.keys(data).find(key => Array.isArray(data[key]));
                if (arrayKey) {
                    statusArray = data[arrayKey];
                    console.log(`📦 Using data.${arrayKey} array`);
                }
            }
            
            if (statusArray && Array.isArray(statusArray)) {
                console.log('🔍 Searching in array with length:', statusArray.length);
                // Find all status records for this specific employee
                const empStatusRecords = statusArray.filter(item => {
                    console.log('Comparing:', item.emp_id, '===', empId);
                    return item.emp_id?.toString() === empId?.toString();
                });
                
                if (empStatusRecords.length > 0) {
                    console.log('✅ Found org status records for employee:', empId, empStatusRecords);
                    return empStatusRecords; // Return array of all records
                } else {
                    console.log('⚠️ No matching status found for emp_id:', empId);
                }
            } else {
                console.log('⚠️ Status array not found in response');
            }
            
            return null;
        } catch (error) {
            console.error('❌ Error fetching org status:', error);
            console.error('❌ Error response:', error.response?.data);
            return null;
        }
    }, [appId, assessmentId, assessorId]);

    // Delete organization status
    const deleteOrgStatus = useCallback(async (recordId) => {
        try {
            const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Deleteorgstatus`;
            
            const params = {
                appid: appId?.toString() || '',
                assessmentid: assessmentId?.toString() || '',
                statusby: assessorId?.toString() || '',
                id: recordId?.toString() || ''
            };
            
            console.log('🗑️ Calling Deleteorgstatus API:', url);
            console.log('📦 Params:', params);
            
            const response = await axios.delete(url, { params });
            
            console.log('📥 Delete response status:', response.status);
            console.log('📥 Delete response data:', response.data);
            
            return { success: response.status === 200 || response.status === 204, data: response.data };
        } catch (error) {
            console.error('❌ Error deleting org status:', error);
            console.error('❌ Error details:', error.response?.data || error.message);
            return { success: false, error: error.message };
        }
    }, [appId, assessmentId, assessorId]);

    // Save organization status for staff adequacy
    const saveOrgStatus = useCallback(async (empId, disciplineId, groupId, status, remarks, clauseNo = '') => {
        try {
            const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Saveorgstatus`;
            
            const payload = {
                appid: appId?.toString() || '',
                assessmentid: assessmentId?.toString() || '',
                emp_id: empId?.toString() || '',
                disciplineid: disciplineId?.toString() || '',
                groupid: groupId?.toString() || '',
                status: status || '',
                remarks: remarks || '',
                statusby: assessorId?.toString() || '',
                clauseNo: clauseNo || ''
            };
            
            console.log('📤 Calling Saveorgstatus API:', url);
            console.log('📦 Payload:', payload);
            console.log('🔍 Current assessorId:', assessorId);
            console.log('🔍 Current appId:', appId);
            console.log('🔍 Current assessmentId:', assessmentId);
            
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            
            console.log('📥 Response status:', response.status);
            console.log('📥 Response data:', response.data);
            
            return { success: response.status === 200 || response.status === 201, data: response.data };
        } catch (error) {
            console.error('❌ Error saving org status:', error);
            console.error('❌ Error details:', error.response?.data || error.message);
            return { success: false, error: error.message };
        }
    }, [appId, assessmentId, assessorId]);

    const fetchStaffList = useCallback(async () => {
        if (!appId || !orgId) return;

        setLoading(true);

        try {
            console.log('📄 LabStaffSection - Fetching lab staff with appId:', appId, 'orgId:', orgId);
            const response = await TestingLaboratoryService.getLabStaff(appId, orgId);
            if (response.success) {
                setStaffList(response.data || []);
            } else {
                setError(response.message || 'Failed to load staff list');
            }
        } catch (error) {
            setError('Failed to load staff list');
        } finally {
            setLoading(false);
        }
    }, [appId, orgId]);

    const getApplicationStage = async (appId) => {
        try {
            if (!appId) return;

            console.log('🔄 LabStaffSection - Fetching application stage with appId:', appId);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
            const result = await response.json();

            if (result.success) {
                setApplicationStage(result.stage);
            } else {
                console.log('Stage not found, defaulting to stage 1 for demo purposes');
                setApplicationStage(1); // Default to 1 for demo purposes
            }
        } catch (error) {
            console.error('Error fetching application stage:', error);
            console.log('Error occurred, defaulting to stage 1 for demo purposes');
            setApplicationStage(1); // Default to 1 for demo purposes
        }
    };

    // Load DA-NC data function
    const loadDANCData = useCallback(async () => {
        if (!orgId || !appId) return;

        try {
            // Determine expected NCtype based on userTypeId
            const userTypeId = localStorage.getItem('userTypeId');
            let expectedNCType = 'Document Adequacy'; // Default

            if (userTypeId === '4') {
                expectedNCType = 'Document Adequacy';
            } else if (userTypeId === '6') {
                expectedNCType = 'Assessment';
            }

            console.log(`🔍 LabStaffSection - Loading DANC data for userType ${userTypeId} with expected NCtype: ${expectedNCType}`);

            // Get all DA NC records for this org and app
            const response = await getDANC({
                orgid: orgId,
                appid: appId,
                assessorId: assessorId,
                isactive: 1,
            });

            console.log('Staff DA-NC API Response:', response);

            if (
                response.data.success &&
                response.data.data &&
                response.data.data.length > 0
            ) {
                console.log(`📋 Found ${response.data.data.length} total records for LabStaffSection`);

                // Filter Records for Staff section and by NCtype
                const staffDANCRecords = response.data.data.filter(
                    (record) =>
                        (record.SectionName === 'Staff' ||
                            record.SectionName === 'Lab Staff' ||
                            record.SectionName === 'Laboratory Staff') &&
                        record.NCtype === expectedNCType
                );

                console.log(`🎯 Filtered to ${staffDANCRecords.length} staff records with NCtype: ${expectedNCType}`);

                setDancRecords(staffDANCRecords);

                // Create adequacy checks from DA NC data
                const dancAdequacyChecks = {};

                staffDANCRecords.forEach((record) => {
                    let sectionKey = record.sourceid; // Use sourceid as the key for staff

                    if (sectionKey) {
                        dancAdequacyChecks[sectionKey] = {
                            status: record.InformationStatus || '',
                            remarks: record.InformationProvided || record.Observation || '',
                            ClauseNo: record.ClauseNo || '',
                            isSubmitted: false, // Allow users to modify and resubmit
                            submittedAt: record.updatedate || record.Insertdate,
                            dancRecord: record, // Store original record for reference
                        };

                        console.log(
                            `Mapped staff Record to staff ID "${sectionKey}":`,
                            {
                                status: record.InformationStatus,
                                remarks: record.InformationProvided || record.Observation,
                                sourceid: record.sourceid,
                            }
                        );
                    }
                });

                // Merge with existing adequacy checks
                setAdequacyChecks((prev) => {
                    const merged = {
                        ...prev,
                        ...dancAdequacyChecks,
                    };
                    console.log('Final merged staff adequacy checks:', merged);
                    return merged;
                });

                // showSnackbar(
                //   `${staffDANCRecords.length} staff Records loaded and bound to adequacy checks`,
                //   'success'
                // );
            } else {
                console.log('No staff Records found in response');
                setDancRecords([]);
            }
        } catch (error) {
            console.error('Error loading staff DA-NC data:', error);
            //   showSnackbar('Error loading DA-NC data: ' + error.message, 'error');
        }
    }, [orgId, appId]);

    // Load unfreeze records
    const loadUnfreezeRecords = useCallback(async () => {
        if (!appId) return;

        try {
            const response = await UnfreezeApiService.getUnfreezeRecords(appId, orgId);
            if (response.success) {
                console.log('Unfreeze records loaded:', response.data);
                setUnfreezeRecords(response.data);
            }
        } catch (err) {
            console.error("Load unfreeze records error:", err);
        }
    }, [appId, orgId]);

    // Helper function to check if a section is unfrozen based on API data
    const isSectionUnfrozen = (sectionTitle) => {
        if (!unfreezeRecords || unfreezeRecords.length === 0) return false;

        const today = new Date();
        // Reset time to start of day for date-only comparison
        today.setHours(0, 0, 0, 0);

        return unfreezeRecords.some(record => {
            if (record.sectionname !== sectionTitle) return false;

            const expiryDate = new Date(record.expirydate);
            // Reset expiry date time to start of day for date-only comparison
            expiryDate.setHours(0, 0, 0, 0);

            // Return true if expiry date is today or in the future (still valid for editing)
            // Compare dates only, not timestamps
            return expiryDate.getTime() >= today.getTime();
        });
    };

    // Fetch scrutiny remarks from API

    const fetchScrutinyRemarks = useCallback(async () => {
        const type = localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review';
        try {
            setScrutinyRemarksLoading(true);
            console.log('Fetching scrutiny remarks for Lab Staff section...');

            const documentRemarksapi = await getScrutinyRemarks({
                sectionName: 'Staff',
                type: type
            });
            const documentRemarks = documentRemarksapi.data;

            if (documentRemarks.success && documentRemarks.data) {
                setScrutinyRemarks(documentRemarks.data);
                console.log('Loaded scrutiny remarks:', documentRemarks.data.length);
            } else {
                console.warn('No scrutiny remarks found or API returned success: false');
                setScrutinyRemarks([]);
            }
        } catch (error) {
            console.error('Error fetching scrutiny remarks:', error);
            setScrutinyRemarks([]);
        } finally {
            setScrutinyRemarksLoading(false);
        }
    }, []);
     

    // Fetch clause numbers from API
    const fetchClauseNo = useCallback(async () => {
        try {
             const decryptedCapacity = getCapacityFromQuery();
            console.log('Fetching clause numbers for Lab Staff section...');

            const clauseResponse = await getClauseNo({ 
                        standard: '2022', 
                        labType: 'Medical', 
                        userType: decryptedCapacity, 
                        appid: appId, 
                        orgid: orgId 
                      });

            if (clauseResponse.data?.isstatus && clauseResponse.data?.clause) {
                setClauseNo(clauseResponse.data.clause);
                console.log('Loaded clause numbers:', clauseResponse.data.clause.length);
            } else {
                console.warn('No clause numbers found or API returned isstatus: false');
                setClauseNo([]);
            }
        } catch (error) {
            console.error('Error fetching clause numbers:', error);
            setClauseNo([]);
        }
    }, []);

    // Fetch disciplines from API on component mount
    useEffect(() => {
        if (appId && orgId) {
            getApplicationStage(appId);
            fetchDisciplines();
            fetchStaffList();
            fetchScrutinyRemarks();
            fetchClauseNo();
        } else {
            setError('Application ID and Organization ID are required. Please ensure you are logged in properly.');
        }
    }, [fetchDisciplines, fetchStaffList, fetchScrutinyRemarks, fetchClauseNo, appId, orgId]);

    // Load DA-NC data when form data is available
    useEffect(() => {
        if (orgId && appId) {
            loadDANCData();
            loadUnfreezeRecords();
        }
    }, [orgId, appId, refreshTrigger, loadDANCData, loadUnfreezeRecords]);

    // Load org statuses for all staff members
    useEffect(() => {
        const loadAllStaffOrgStatuses = async () => {
            if (staffList.length === 0) return;
            
            console.log('🔄 Loading org statuses for all staff members...');
            const statusMap = {};
            
            for (const staff of staffList) {
                try {
                    const records = await fetchOrgStatus(staff.Id);
                    if (records && Array.isArray(records) && records.length > 0) {
                        statusMap[staff.Id] = records;
                        console.log(`✅ Loaded ${records.length} org status records for staff ${staff.Id}`);
                    }
                } catch (error) {
                    console.error(`Error loading org status for staff ${staff.Id}:`, error);
                }
            }
            
            setStaffOrgStatuses(statusMap);
            console.log('✅ Finished loading org statuses for all staff');
        };
        
        loadAllStaffOrgStatuses();
    }, [staffList, fetchOrgStatus, refreshTrigger]);

    // Adjust tab index based on applicationStage once it's loaded
    useEffect(() => {
        const requestedTab = sessionStorage.getItem('staff_requested_tab');
        if (requestedTab !== null && applicationStage !== null) {
            const tabIndex = parseInt(requestedTab);

            // Map the original tab indices to the new indices based on stage
            let adjustedTab = tabIndex;

            // When stage is 18/""/null/"18a"/"18b", tabs are: 0=Add/Edit Staff, 1=View Staff (both tabs visible)
            // When stage is other (not null/""/"18"/"18a"/"18b"), tabs are: 0=View Staff (only 1 tab visible)

            if (applicationStage !== null && applicationStage !== 'null' && applicationStage !== '' && applicationStage !== '18' && applicationStage !== '18a' && applicationStage !== '18b' || isSectionUnfrozen('Lab Staff')) {
                // When stage is not null/""/"18"/"18a"/"18b", tabs are: 0=View Staff
                // Original tabs were: 0=Add/Edit Staff, 1=View Staff
                if (tabIndex === 0) {
                    // Add/Edit Staff doesn't exist when stage is not null/""/"18"/"18a"/"18b", go to View Staff
                    adjustedTab = 0;
                }
                // tabIndex 1 (View Staff) stays at 0
                else if (tabIndex === 1) {
                    adjustedTab = 0;
                }
            }
            // For null/""/"18"/"18a", both tabs are visible so no adjustment needed

            setTabValue(adjustedTab);
            sessionStorage.removeItem('staff_requested_tab');

            console.log('🔄 Adjusted staff tab index:', {
                requested: tabIndex,
                adjusted: adjustedTab,
                applicationStage
            });
        } else if (applicationStage !== null) {
            // On initial load (no sessionStorage), ensure View Staff tab is shown
            // When stage is not null/""/"18"/"18a"/"18b", View Staff is at index 0
            // When stage is null/""/"18"/"18a"/"18b", View Staff is at index 1
            if (applicationStage !== 'null' && applicationStage !== '' && applicationStage !== '18' && applicationStage !== '18a' && applicationStage !== '18b' && !isSectionUnfrozen('Lab Staff')) {
                setTabValue(0); // Only 1 tab visible, View Staff is at index 0
            } else {
                setTabValue(1); // 2 tabs visible, View Staff is at index 1
            }
        }
    }, [applicationStage]);

    // Handle discipline matching when disciplines are loaded and we have editing staff
    useEffect(() => {
        if (editingStaff && disciplines.length > 0 && editingStaff.DisciplineId) {
            // Handle both single discipline ID and comma-separated discipline IDs
            const tableDisciplineIds = editingStaff.DisciplineId.toString().split(',')
                .map(id => id.trim())
                .filter(id => id !== '');

            // Match table discipline IDs with available disciplines
            const matchedDisciplines = [];
            tableDisciplineIds.forEach(tableDisciplineId => {
                const matchingDiscipline = disciplines.find(discipline => {
                    const disciplineIdStr = (discipline.DisciplineId || discipline.Id).toString();
                    const tableIdStr = tableDisciplineId.toString();
                    return disciplineIdStr === tableIdStr;
                });

                if (matchingDiscipline) {
                    const disciplineId = matchingDiscipline.DisciplineId || matchingDiscipline.Id;
                    matchedDisciplines.push(disciplineId);
                }
            });

            setSelectedDisciplines(matchedDisciplines);
        }
    }, [disciplines, editingStaff]);


    const getAssessorDisciplines = async (assessorId, appId) => {
        try {
            const response = await TestingLaboratoryService.assessorDiscipline(assessorId, appId);
            if (response.success) {
                return response.data;
            }
        } catch (error) {
            console.error('Error fetching assessor disciplines:', error);
        }
        return [];
    };

    useEffect(() => {
        const fetchData = async () => {
            const disciplines = await getAssessorDisciplines(assessorId, appId);
            setAssessorDisciplines(disciplines);
        };
        fetchData();
    }, [assessorId, appId]);

   const assessorDisciplineIds = useMemo(() => {
     return assessorDisciplines
       ?.flatMap(item =>
         String(item.Disciplines || '')
           .split(',')
           .map(id => id.trim())
           .filter(Boolean)
       );
   }, [assessorDisciplines]);
   
    const assessorGroupsIds = useMemo(() => {
     return assessorDisciplines
       ?.flatMap(item =>
         String(item.Groups || '')
           .split(',')
           .map(id => id.trim())
           .filter(Boolean)
       );
   }, [assessorDisciplines]);
   
   const uniqueDisciplineOptions = useMemo(() => {
     const seen = new Set();
     return disciplines.filter((discipline) => {
       const disciplineId = (discipline.DisciplineId || discipline.Id)?.toString();
       if (!disciplineId) return false;
       if (!assessorDisciplineIds?.includes(disciplineId)) return false;
       if (seen.has(disciplineId)) return false;
       seen.add(disciplineId);
       return true;
     });
   }, [disciplines, assessorDisciplineIds]);
   
   const uniqueGroupOptions = useMemo(() => {
     const seen = new Set();
     return disciplines
       .filter((discipline) => {
         if (!selectedDisciplineForRow) return false;
         const disciplineId = (discipline.DisciplineId || discipline.Id)?.toString();
         return disciplineId === selectedDisciplineForRow?.toString();
       })
       .filter((discipline) => {
         const groupId = discipline.GroupId?.toString();
         if (!groupId || seen.has(groupId)) return false;
         seen.add(groupId);
         return true;
       });
   }, [disciplines, selectedDisciplineForRow]);

    const filteredData = useMemo(() => {
        const capacity = getCapacityFromQuery();

        // Helper: staff discipline list
      const getStaffDisciplineIds = (staff) =>
  staff.DisciplineId
    ? String(staff.DisciplineId)
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
    : [];

        // =======================
        // TA → ONLY Authorized Signatory + Discipline match
        // =======================
        if (capacity === "2") {
            return staffList.filter(staff =>
                staff.IsAuthorisedSignatory === true &&
                getStaffDisciplineIds(staff).some(id =>
                    assessorDisciplineIds.includes(id)
                )
            );
        }

        // =======================
        // LA → ALL EXCEPT Authorized Signatory & Technical Expert
        // =======================
        if (capacity === "1") {
            console.log("Filtering for LA - excluding Authorized Signatory");
            return staffList.filter(staff =>
                staff.IsAuthorisedSignatory !== true
            );
        }

        // =======================
        // NLA → Non-Authorized + Discipline match
        // =======================
        if (capacity === "2") {
            return staffList.filter(staff =>
                staff.IsAuthorisedSignatory === false &&
                getStaffDisciplineIds(staff).some(id =>
                    assessorDisciplineIds.includes(id)
                )
            );
        }
        if (capacity === "3") {
            return staffList.filter(staff =>
                getStaffDisciplineIds(staff).some(id =>
                    assessorDisciplineIds.includes(id)
                )
            );
        }

        return staffList;
    }, [staffList, assessorDisciplineIds, location.search]);



    console.log("Filtered Data:", filteredData);
    const handleFileChange = (fieldName, fileData) => {
        if (fileData) {
            // Extract file path and name for database storage
            const fileName = fileData.originalName || fileData.fileName || fileData.name || '';
            const filePath = fileData.key || fileData.fileName || '';
            const signedUrl = fileData.signedUrl || fileData.url || '';

            setFileData(prev => ({
                ...prev,
                specimenSignature: {
                    fileName: fileName,
                    filePath: filePath,
                    key: filePath,
                    originalName: fileName,
                    signedUrl: signedUrl,
                    storage: 'S3'
                }
            }));
        } else {
            // Clear file data when file is removed
            setFileData(prev => ({
                ...prev,
                specimenSignature: null
            }));
        }
    };

    const saveLabStaff = async () => {
        if (!appId || !orgId) {
            showSnackbar('Application ID and Organization ID are required', 'error');
            return;
        }

        // Validate required fields
        if (!employeeType || employeeType === '') {
            showSnackbar('Please select an Employee Type', 'warning');
            return;
        }

        if (!formData.name || !formData.designation || !formData.qualification ||
            !formData.specialization || !formData.experience || !formData.training ||
            !formData.mobileNo || !formData.email || !formData.reportReview) {
            showSnackbar('Please fill in all required fields', 'warning');
            return;
        }

        // Validate mobile number - must be exactly 10 digits and numeric only
        if (formData.mobileNo.length !== 10 || !/^\d{10}$/.test(formData.mobileNo)) {
            showSnackbar('Mobile number must be exactly 10 digits and contain only numbers', 'warning');
            return;
        }

        // Validate email format
        if (!isValidEmail(formData.email)) {
            showSnackbar('Please enter a valid email address', 'warning');
            return;
        }

        // Additional validation when Report/Review is Yes
        if (formData.reportReview === 'Yes') {
            if (!formData.partFullTime) {
                showSnackbar('Please select Part time / Full time employment type', 'warning');
                return;
            }

            if (formData.partFullTime === 'Part Time' && !formData.timing) {
                showSnackbar('Please specify timing for part-time employment', 'warning');
                return;
            }

            if (!fileData.specimenSignature || !fileData.specimenSignature.fileName) {
                showSnackbar('Please upload specimen signature', 'warning');
                return;
            }
        }

        try {
            setLoading(true);

            // Prepare lab staff data for API
            const labStaffData = {
                appId: parseInt(appId),
                orgId: parseInt(orgId),
                employeeType: employeeType,
                name: `${formData.salutation} ${formData.name}`.trim(),
                designation: formData.designation,
                qualification: formData.qualification,
                specialization: formData.specialization,
                relevantExperience: formData.experience,
                relevantTraining: formData.training,
                contactNo: formData.mobileNo,
                faxNo: formData.landlineNo || '',
                emailID: formData.email,
                done17025Training: 0, // Default value
                trainingCertFile: '', // Can be extended later
                isAuthorisedSignatory: formData.reportReview === 'Yes' ? 1 : 0,
                disciplineId: selectedDisciplines.length > 0 ? selectedDisciplines.join(',') : null, // Multiple disciplines as comma-separated
                groupId: null,
                signatorySign: formData.reportReview === 'Yes' && fileData.specimenSignature ?
                    (fileData.specimenSignature.signedUrl || fileData.specimenSignature.filePath) : '',
                remark: '',
                typeofEngagement: formData.reportReview === 'Yes' ? formData.partFullTime : '',
                timming: formData.reportReview === 'Yes' && formData.partFullTime === 'Part Time' ? formData.timing : '',
                panNo: '',
                panDoc: '',
                photo: '',
                aadhar: '',
                aerbNo: '',
                aerbDocFile: ''
            };

            const response = editingStaff ?
                await TestingLaboratoryService.updateLabStaff(editingStaff.Id, labStaffData) :
                await TestingLaboratoryService.createLabStaff(labStaffData);

            if (response.success) {
                showSnackbar(
                    editingStaff ? 'Lab staff details updated successfully!' : 'Lab staff details saved successfully!',
                    'success'
                );

                clearForm(); // Clear form after successful save/update
                fetchStaffList(); // Refresh staff list
            } else {
                showSnackbar(response.message || 'Failed to save lab staff details', 'error');
            }
        } catch (error) {
            showSnackbar(error.message || 'Failed to save lab staff details', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleEditStaff = async (staff) => {
        setEditingStaff(staff);
        setTabValue(0); // Switch to Add Staff tab

        // Extract salutation and name from the full name
        const nameParts = staff.Name.split(' ');
        const salutation = nameParts[0].endsWith('.') ? nameParts[0] : '';
        const name = salutation ? nameParts.slice(1).join(' ') : staff.Name;

        // Populate form with staff data based on API response format
        setEmployeeType(staff.EmployeeType || '');
        setFormData({
            salutation: salutation,
            name: name,
            designation: staff.Designation || '',
            qualification: staff.Qualification || '',
            specialization: staff.Specialization || '',
            experience: staff.RelevantExperience || '',
            training: staff.RelevantTraining || '',
            landlineNo: staff.FaxNo || '',
            mobileNo: staff.ContactNo || '',
            email: staff.EmailID || '',
            reportReview: staff.IsAuthorisedSignatory ? 'Yes' : 'No',
            partFullTime: staff.typeofEngagement || '',
            timing: staff.Timming || ''
        });

        // Set selected disciplines if available - match table discipline IDs to available disciplines
        if (staff.DisciplineId) {
            // Handle both single discipline ID and comma-separated discipline IDs
            // Convert to string first, then split and parse
            const tableDisciplineIds = staff.DisciplineId.toString().split(',')
                .map(id => id.trim())
                .filter(id => id !== '');

            // Check if disciplines are available before matching
            if (disciplines && disciplines.length > 0) {
                // Match table discipline IDs with available disciplines and check corresponding checkboxes
                const matchedDisciplines = [];
                tableDisciplineIds.forEach(tableDisciplineId => {
                    // Find matching discipline in available disciplines list
                    // Compare as strings to avoid type mismatch issues
                    const matchingDiscipline = disciplines.find(discipline => {
                        const disciplineIdStr = (discipline.DisciplineId || discipline.Id).toString();
                        const tableIdStr = tableDisciplineId.toString();
                        return disciplineIdStr === tableIdStr;
                    });

                    if (matchingDiscipline) {
                        // Use the correct ID property from the matching discipline - keep as string or number as needed
                        const disciplineId = matchingDiscipline.DisciplineId || matchingDiscipline.Id;
                        matchedDisciplines.push(disciplineId);
                    }
                });

                setSelectedDisciplines(matchedDisciplines);
            } else {
                // If disciplines aren't loaded yet, try direct match - keep as strings
                setSelectedDisciplines(tableDisciplineIds);
            }
        } else {
            setSelectedDisciplines([]);
        }

        // Set file data if available - only set if there's actually a file
        // Use S3 utils for enhanced processing
        const processedSignature = staff.SignatorySign ?
            await s3Utils.processStaffSignatureData(staff) : null;

        setFileData({
            specimenSignature: processedSignature
        });
    };

    const handleDeleteStaff = async (staffId) => {
        setLoading(true);
        try {
            const response = await TestingLaboratoryService.deleteLabStaff(staffId);
            if (response.success) {
                showSnackbar('Staff member deleted successfully', 'success');
                fetchStaffList(); // Refresh the list
            } else {
                showSnackbar(response.message || 'Failed to delete staff member', 'error');
            }
        } catch (error) {
            showSnackbar(error.message || 'Failed to delete staff member', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDisciplineChange = useCallback((disciplineId, disciplineName, isChecked) => {
        if (isChecked) {
            setSelectedDisciplines(prev => [...prev, disciplineId]);
        } else {
            setSelectedDisciplines(prev => prev.filter(id => id !== disciplineId));
        }
    }, []);

    const handleEmployeeTypeChange = useCallback((event) => {
        const value = event.target.value;
        setEmployeeType(value);
    }, []);

    // Email validation helper function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Memoized form field change handler
    const handleFormDataChange = useCallback((field, value) => {
        // Mobile number validation - only allow 10 digit numbers
        if (field === 'mobileNo') {
            // Remove any non-digit characters
            const cleanValue = value.replace(/\D/g, '');
            // Limit to 10 digits
            const limitedValue = cleanValue.slice(0, 10);
            setFormData(prev => ({
                ...prev,
                [field]: limitedValue
            }));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // Memoized clear form function
    const clearForm = useCallback(() => {
        setEditingStaff(null);
        setEmployeeType('');
        setFormData({
            salutation: '',
            name: '',
            designation: '',
            qualification: '',
            specialization: '',
            experience: '',
            training: '',
            landlineNo: '',
            mobileNo: '',
            email: '',
            reportReview: '',
            partFullTime: '',
            timing: ''
        });
        setSelectedDisciplines([]);
        setFileData({
            specimenSignature: null
        });
    }, []);

    // Memoized tab change handler
    const handleTabChange = useCallback((event, newValue) => {
        setTabValue(newValue);
        // When applicationStage is null/""/18/18a, "View Staff" is at index 1
        // When applicationStage is other, "View Staff" is at index 0
        const isViewStaffTab = (applicationStage === null || applicationStage === "" || applicationStage === '18a' || applicationStage === '18' || applicationStage === '18b' || isSectionUnfrozen('Lab Staff')) ? newValue === 1 : newValue === 0;
        if (isViewStaffTab) {
            fetchStaffList(); // Refresh staff list when switching to View Staff tab
        }
    }, [fetchStaffList, applicationStage]);

    // Memoized Add Staff Tab Component
    const renderAddStaffTab = useCallback(() => (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {editingStaff && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Editing staff member: {editingStaff.Name}
                    <Button
                        size="small"
                        onClick={clearForm}
                        sx={{ ml: 2 }}
                    >
                        Cancel Edit
                    </Button>
                </Alert>
            )}

            {/* Employee Type Dropdown */}
            <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel id="employee-type-label">Employee Type</InputLabel>
                        <Select
                            labelId="employee-type-label"
                            value={employeeType}
                            label="Employee Type"
                            onChange={handleEmployeeTypeChange}
                        >
                            <MenuItem value="">Select Employee Type</MenuItem>
                            <MenuItem value="Details of Radiological Safety Officer (For Medical Imaging laboratories)">Details of Radiological Safety Officer (For Medical Imaging laboratories)</MenuItem>
                            <MenuItem value="Head of the laboratory having overall responsibility">Head of the laboratory having overall responsibility</MenuItem>
                            <MenuItem value="Laboratory Director">Laboratory Director</MenuItem>
                            <MenuItem value="Person responsible for management system">Person responsible for management system</MenuItem>
                            <MenuItem value="Person Responsible for Technical Operations">Person Responsible for Technical Operations</MenuItem>
                            <MenuItem value="Others">Others</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 1.5 }}>
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                            Disciplines
                        </Typography>

                        {disciplinesLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <FormGroup>
                                <Grid container sx={{ maxHeight: '200px', overflowY: 'auto' }} spacing={2}>
                                    {disciplines.map((discipline) => {
                                        // Use consistent ID property - prefer DisciplineId, fallback to Id
                                        const disciplineId = discipline.DisciplineId || discipline.Id;

                                        // Check if this discipline is selected - handle both string and number comparisons
                                        const isChecked = selectedDisciplines.some(selectedId => {
                                            return selectedId.toString() === disciplineId.toString();
                                        });

                                        return (
                                            <Grid item xs={12} key={disciplineId}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={(e) => handleDisciplineChange(disciplineId, discipline.DisciplineName, e.target.checked)}
                                                            name={`discipline_${disciplineId}`}
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {discipline.DisciplineName}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                    sx={{
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: 1,
                                                        p: 1,
                                                        margin: 0,
                                                        width: '100%',
                                                        '&:hover': {
                                                            backgroundColor: '#f5f5f5'
                                                        },
                                                        ...(isChecked && {
                                                            backgroundColor: '#e3f2fd',
                                                            borderColor: '#2196f3'
                                                        })
                                                    }}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {disciplines.length === 0 && !disciplinesLoading && (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                        No disciplines available
                                    </Typography>
                                )}
                            </FormGroup>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Personal Details Form */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    Personal Details
                </Typography>

                <Grid container spacing={3}>
                    {/* Row 1 */}
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Salutation</InputLabel>
                            <Select
                                value={formData.salutation}
                                label="Salutation"
                                onChange={(e) => handleFormDataChange('salutation', e.target.value)}
                            >
                                <MenuItem value="Mr.">Mr.</MenuItem>
                                <MenuItem value="Ms.">Ms.</MenuItem>
                                <MenuItem value="Dr.">Dr.</MenuItem>
                                <MenuItem value="Prof.">Prof.</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={9}>
                        <TextField
                            fullWidth
                            label="Name"
                            value={formData.name}
                            onChange={(e) => handleFormDataChange('name', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Designation"
                            value={formData.designation}
                            onChange={(e) => handleFormDataChange('designation', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Qualification"
                            value={formData.qualification}
                            onChange={(e) => handleFormDataChange('qualification', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Row 3 */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Specialization"
                            value={formData.specialization}
                            onChange={(e) => handleFormDataChange('specialization', e.target.value)}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Relevant Experience (in years)"
                            value={formData.experience}
                            onChange={(e) => handleFormDataChange('experience', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Row 4 */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Relevant Training"
                            value={formData.training}
                            onChange={(e) => handleFormDataChange('training', e.target.value)}
                            multiline
                            rows={3}
                            required
                        />
                    </Grid>

                    {/* Row 5 */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Landline No."
                            value={formData.landlineNo}
                            onChange={(e) => handleFormDataChange('landlineNo', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Mobile No."
                            value={formData.mobileNo}
                            onChange={(e) => handleFormDataChange('mobileNo', e.target.value)}
                            required
                            error={formData.mobileNo && formData.mobileNo.length !== 10}
                            helperText={
                                formData.mobileNo && formData.mobileNo.length !== 10
                                    ? 'Mobile number must be exactly 10 digits'
                                    : "Enter 10-digit mobile number"
                            }
                            inputProps={{
                                maxLength: 10,
                                pattern: "[0-9]*",
                                inputMode: "numeric"
                            }}
                        />
                    </Grid>

                    {/* Row 6 */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Email ID"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleFormDataChange('email', e.target.value)}
                            required
                            error={formData.email && !isValidEmail(formData.email)}
                            helperText={
                                formData.email && !isValidEmail(formData.email)
                                    ? "Please enter a valid email address"
                                    : "Enter a valid email address (e.g., user@example.com)"
                            }
                        />
                    </Grid>

                    {/* Report/Review Authorization */}
                    <Grid item xs={12}>
                        <FormControl component="fieldset">
                            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                                Report, Review and Authorization *
                            </FormLabel>
                            <RadioGroup
                                row
                                value={formData.reportReview}
                                onChange={(e) => handleFormDataChange('reportReview', e.target.value)}
                            >
                                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                <FormControlLabel value="No" control={<Radio />} label="No" />
                            </RadioGroup>
                        </FormControl>
                    </Grid>

                    {/* Conditional fields when Report/Review is Yes */}
                    {formData.reportReview === 'Yes' && (
                        <>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Part time / Full time *</InputLabel>
                                    <Select
                                        value={formData.partFullTime}
                                        label="Part time / Full time *"
                                        onChange={(e) => handleFormDataChange('partFullTime', e.target.value)}
                                    >
                                        <MenuItem value="Full Time">Full Time</MenuItem>
                                        <MenuItem value="Part Time">Part Time</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {formData.partFullTime === 'Part Time' && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Timing *"
                                        value={formData.timing}
                                        onChange={(e) => handleFormDataChange('timing', e.target.value)}
                                        placeholder="e.g., 9 AM to 1 PM"
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                        Specimen Signature Upload *
                                    </Typography>
                                    <FileUploadComponent
                                        fieldName="specimenSignature"
                                        label="Upload Specimen Signature"
                                        orgId={orgId}
                                        section="staff"
                                        accept=".jpg,.jpeg,.png"
                                        maxSize={5}
                                        required={false}
                                        onChange={(fieldName, fileData) => handleFileChange(fieldName, fileData)}
                                        initialFile={
                                            fileData.specimenSignature?.fileName || fileData.specimenSignature?.filePath
                                                ? fileData.specimenSignature
                                                : null
                                        }
                                    />
                                </Paper>
                            </Grid>
                        </>
                    )}

                    {/* Save Button */}
                    {(applicationStage === null || applicationStage === "" || applicationStage === '18a' || isSectionUnfrozen('Lab Staff')) && (
                        <Grid item xs={12} sx={{ mt: 3 }}>
                            <Button
                                variant="contained"
                                onClick={saveLabStaff}
                                disabled={loading}
                                sx={{
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    px: 4,
                                    py: 1.5,
                                    fontWeight: 600
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                        {editingStaff ? 'Updating...' : 'Saving...'}
                                    </>
                                ) : (
                                    editingStaff ? 'Update Staff' : 'Save Staff'
                                )}
                            </Button>
                            {editingStaff && (
                                <Button
                                    variant="outlined"
                                    onClick={clearForm}
                                    sx={{ ml: 2, px: 4, py: 1.5 }}
                                >
                                    Cancel
                                </Button>
                            )}
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Box>
    ), [
        error,
        editingStaff,
        employeeType,
        disciplinesLoading,
        disciplines,
        selectedDisciplines,
        formData,
        loading,
        fileData,
        handleEmployeeTypeChange,
        handleDisciplineChange,
        handleFormDataChange,
        clearForm,
        orgId
    ]);

    // Checkbox handlers
    const handleSelectItem = useCallback((itemId) => {
        setSelectedItems(prev => {
            if (prev.includes(itemId)) {
                return prev.filter(id => id !== itemId);
            } else {
                return [...prev, itemId];
            }
        });
    }, []);

    const handleSelectAll = useCallback((event) => {
        if (event.target.checked) {
            setSelectedItems(filteredData.map(item => item.Id));
        } else {
            setSelectedItems([]);
        }
    }, [filteredData]);

    // Helper function to get adequacy status for a staff member
    const getStaffStatus = (staffId) => {
        return adequacyChecks[staffId] || { status: '', remarks: '', ClauseNo: '' };
    };

    // Helper function to get all org status records for a staff member
    const getStaffOrgStatus = useCallback(async (staffId) => {
        try {
            const records = await fetchOrgStatus(staffId);
            return Array.isArray(records) ? records : [];
        } catch (error) {
            console.error('Error fetching org status for staff:', staffId, error);
            return [];
        }
    }, [fetchOrgStatus]);

    // Adequacy popup handlers
    const handleMarkAdequate = useCallback(() => {
        if (selectedItems.length === 0) {
            showSnackbar('Please select at least one staff member to mark as adequate', 'warning');
            return;
        }
        setAdequacyDialogOpen(true);
    }, [selectedItems]);

    // Handler for marking individual row adequate - opens discipline-wise dialog
    const handleMarkAdequateRow = useCallback(async (staffId) => {
        setSelectedStaffForDiscipline(staffId);
        
        // Fetch org status for this staff member
        try {
            const existingRecords = await fetchOrgStatus(staffId);
            if (existingRecords && Array.isArray(existingRecords)) {
                setDisciplineOrgStatusRecords(existingRecords);
                // Also update the staffOrgStatuses cache
                setStaffOrgStatuses(prev => ({
                    ...prev,
                    [staffId]: existingRecords
                }));
            } else {
                setDisciplineOrgStatusRecords([]);
            }
        } catch (error) {
            console.error('Error fetching org status:', error);
            setDisciplineOrgStatusRecords([]);
        }
        
        setDisciplineDialogOpen(true);
    }, [fetchOrgStatus]);

    const closeAdequacyDialog = useCallback(() => {
        setAdequacyDialogOpen(false);
        setAdequacyStatus('');
        setAdequacyRemarks('');
        setSelectedDisciplineId('');
        setOrgStatusRecords([]);
        setIsDropdownSelection(false);
        setSelectedClauses([]);
        setClauseSearchFilter('');
    }, []);

    // Handlers for discipline-wise adequacy dialog
    const closeDisciplineDialog = useCallback(() => {
        setDisciplineDialogOpen(false);
        setSelectedStaffForDiscipline(null);
        setDisciplineAdequacyStatus('');
        setDisciplineAdequacyRemarks('');
        setSelectedDisciplineForRow('');
        setSelectedGroupForRow('');
        setDisciplineOrgStatusRecords([]);
        setSelectedDisciplineClauses([]);
        setDisciplineClauseSearchFilter('');
    }, []);

    const handleDisciplineClauseCheckboxChange = useCallback((clause, isChecked) => {
        setSelectedDisciplineClauses(prev => {
            let newSelection;
            if (isChecked) {
                const cKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                const exists = prev.some(c => {
                    const existingKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                    return existingKey === cKey;
                });
                if (!exists) {
                    newSelection = [...prev, clause];
                } else {
                    newSelection = [...prev];
                }
            } else {
                const cKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                newSelection = prev.filter(c => {
                    const existingKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                    return existingKey !== cKey;
                });
            }
            
            // Update remarks with selected clause names
            const clauseNames = newSelection.map(c => c?.name || c?.displayName || c?.clauseNo || c).join(', ');
            setDisciplineAdequacyRemarks(clauseNames);
            
            return newSelection;
        });
    }, []);

    const handleDisciplineAdequacySubmit = useCallback(async () => {
        // Basic validation
        if (!disciplineAdequacyStatus || (disciplineAdequacyStatus === 'Inadequate' && (!disciplineAdequacyRemarks?.trim()))) {
            showSnackbar('Please fill all required fields', 'error');
            return;
        }

        // Validate discipline selection
        if (!selectedDisciplineForRow) {
            showSnackbar('Please select a discipline', 'error');
            return;
        }

        if (!selectedStaffForDiscipline) {
            showSnackbar('No staff member selected', 'error');
            return;
        }

        try {
            console.log('=== DISCIPLINE-WISE STAFF ADEQUACY SUBMIT ===');
            
            // Get clause numbers from selected clauses
            const clauseNumbers = selectedDisciplineClauses
                .map(c => c?.clauseNo || c?.name || c?.displayName || c)
                .filter(Boolean)
                .join(', ');

            // Call saveOrgStatus API for the selected staff and discipline
            const orgStatusResult = await saveOrgStatus(
                selectedStaffForDiscipline,
                selectedDisciplineForRow,
                selectedGroupForRow,
                disciplineAdequacyStatus,
                disciplineAdequacyRemarks,
                disciplineAdequacyStatus === 'Inadequate' ? clauseNumbers : ''
            );

            if (orgStatusResult.success) {
                showSnackbar(`Discipline-wise adequacy status saved successfully`, 'success');
                
                // Refresh org status records for this staff
                const updatedRecords = await fetchOrgStatus(selectedStaffForDiscipline);
                if (updatedRecords && Array.isArray(updatedRecords)) {
                    setDisciplineOrgStatusRecords(updatedRecords);
                    // Update the staffOrgStatuses cache
                    setStaffOrgStatuses(prev => ({
                        ...prev,
                        [selectedStaffForDiscipline]: updatedRecords
                    }));
                }
                
                // Reset form fields but keep dialog open
                setDisciplineAdequacyStatus('');
                setDisciplineAdequacyRemarks('');
                setSelectedDisciplineForRow('');
                setSelectedGroupForRow('');
                setSelectedDisciplineClauses([]);
                setDisciplineClauseSearchFilter('');
                
                // Refresh the staff list to show updated status
                await fetchStaffList();
            } else {
                showSnackbar(`Failed to save adequacy status: ${orgStatusResult.message || 'Unknown error'}`, 'error');
            }
        } catch (error) {
            console.error('Error submitting discipline-wise adequacy check:', error);
            showSnackbar(`Error: ${error.message || 'Failed to save adequacy status'}`, 'error');
        }
    }, [disciplineAdequacyStatus, disciplineAdequacyRemarks, selectedDisciplineForRow,selectedGroupForRow, selectedStaffForDiscipline, selectedDisciplineClauses, saveOrgStatus, fetchOrgStatus, fetchStaffList]);

    // Load existing org status when dialog opens - DISABLED for checkbox dialog
    // Checkbox dialog now only uses DANC records, not org status
    useEffect(() => {
        // Do nothing - checkbox dialog doesn't use org status
    }, [adequacyDialogOpen, selectedItems]);

    // Handler for adequacy remarks with dropdown tracking
    // Handle clause checkbox change with auto-population of remarks
    const handleClauseCheckboxChange = useCallback((clause, isChecked) => {
        setSelectedClauses(prev => {
            let newSelection;
            if (isChecked) {
                // Add clause if not already present
                // Use id and name combination for unique identification since multiple clauses can have same clauseNo
                const cKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                const exists = prev.some(c => {
                    const existingKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                    return existingKey === cKey;
                });
                if (!exists) {
                    newSelection = [...prev, clause];
                } else {
                    newSelection = [...prev];
                }
            } else {
                // Remove clause if present
                const cKey = `${clause?.id || ''}-${clause?.name || clause?.displayName || ''}`;
                newSelection = prev.filter(c => {
                    const existingKey = `${c?.id || ''}-${c?.name || c?.displayName || ''}`;
                    return existingKey !== cKey;
                });
            }
            
            // Update remarks with selected clause names
            const clauseNames = newSelection.map(c => c?.name || c?.displayName || c?.clauseNo || c).join(', ');
            setAdequacyRemarks(clauseNames);
            
            return newSelection;
        });
    }, []);

    const handleAdequacyRemarksChange = useCallback((newValue, fromDropdown = false) => {
        setAdequacyRemarks(newValue || '');
        setIsDropdownSelection(fromDropdown);
    }, []);

    const handleAdequacySubmit = useCallback(async () => {
        // Basic validation
        if (!adequacyStatus || (adequacyStatus === 'Inadequate' && (!adequacyRemarks?.trim()))) {
            showSnackbar('Please fill all required fields', 'error');
            return;
        }

        // Additional validation for user type 6 (checkbox selection)
        const userTypeId = localStorage.getItem('userTypeId');
        if (userTypeId === '6' && adequacyStatus === 'Inadequate' && selectedClauses.length === 0) {
            showSnackbar('Please select at least one clause number for inadequate status', 'error');
            return;
        }

        setLoading(true);
        try {
            console.log('=== STAFF ADEQUACY SUBMIT ===');
            console.log('Selected staff IDs:', selectedItems);
            console.log('Adequacy Status:', adequacyStatus);
            console.log('Adequacy Remarks:', adequacyRemarks);
            console.log('Selected Clauses:', selectedClauses);

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            // Process each selected staff member
            for (const staffId of selectedItems) {
                try {
                    // Check if Record exists for this staff
                    const existingRecord = dancRecords.find(
                        (record) =>
                            record.sourceid && parseInt(record.sourceid) === parseInt(staffId)
                    );

                    if (existingRecord) {
                        // Update existing Record
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default

                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }

                        console.log(`💾 Updating DANC record for staff ${staffId}, userType ${userTypeId} with NCtype: ${ncType}`);

                        const updateData = {
                            subsectionName: 'Staff Details',
                            informationStatus: adequacyStatus,
                            observation:
                                adequacyStatus === 'Inadequate'
                                    ? adequacyRemarks || 'Document adequacy marked as Inadequate'
                                    : 'Document adequacy check completed',
                            NCtype: ncType,
                            sourceid: staffId,
                            isactive: 1,
                            ClauseNo: userTypeId === '6' ? selectedClauses.map(c => c?.clauseNo || c?.displayName || c?.name || c).join(', ') : null,
                        };

                        console.log(`Updating Record for staff ${staffId}:`, updateData);

                        // Add assessmentId and assessorId for userType 6 (Assessment)
                        let response;
                        if (userTypeId === '6') {
                            response = await updateDANCBySourceId(
                                orgId,
                                appId,
                                'Staff',
                                staffId,
                                assessmentId,
                                assessorId,
                                updateData
                            );
                        } else {
                            response = await updateDANCBySourceId(
                                orgId,
                                appId,
                                'Staff',
                                staffId,
                                0,
                                localStorage.getItem('orgId'),
                                updateData
                            );
                        }
                        console.log(`Update response for staff ${staffId}:`, response);
                        if (response.data.success) {
                            console.log(`✅ Record updated for staff ${staffId}`);
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to Update');
                        }
                    } else {
                        // Create new Record
                        // Determine NCtype based on userTypeId
                        const userTypeId = localStorage.getItem('userTypeId');
                        let ncType = 'Document Adequacy'; // Default

                        if (userTypeId === '4') {
                            ncType = 'Document Adequacy';
                        } else if (userTypeId === '6') {
                            ncType = 'Assessment';
                        }

                        console.log(`✨ Creating new DANC record for staff ${staffId}, userType ${userTypeId} with NCtype: ${ncType}`);

                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: 'Staff',
                            SubsectionName: 'Staff Details',
                            InformationProvided: adequacyRemarks || '',
                            InformationStatus: adequacyStatus,

                            Observation:
                                adequacyStatus === 'Inadequate'
                                    ? adequacyRemarks || 'Document adequacy marked as Inadequate'
                                    : 'Document adequacy check completed',
                            sourceid: staffId,
                            Insertedby: currentUserId,
                            NCtype: ncType,
                            ClauseNo: userTypeId === '6' ? selectedClauses.map(c => c?.clauseNo || c?.displayName || c?.name || c).join(', ') : null,
                            assessmentId: parseInt(assessmentId) || 0,
                            assessorId: parseInt(assessorId) || localStorage.getItem('orgId')

                        };



                        console.log(`Creating Record for staff ${staffId}:`, dancData);
                        const response = await insertDANC(dancData);

                        if (response.data.success) {
                            console.log(`✅ Record created for staff ${staffId}`);
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to create Record');
                        }
                    }

                    // Update adequacy checks locally
                    setAdequacyChecks((prev) => ({
                        ...prev,
                        [staffId]: {
                            status: adequacyStatus,
                            remarks: adequacyRemarks,
                            ClauseNo: userTypeId === '6' ? selectedClauses.map(c => c?.ClauseNo || c?.DisplayName || c?.Name || c).join(', ') : '',
                            isSubmitted: true,
                            submittedAt: new Date().toISOString(),
                        },
                    }));
                } catch (error) {
                    console.error(`Error processing staff ${staffId}:`, error);
                    errors.push(`Staff ${staffId}: ${error.message}`);
                    errorCount++;
                }
            }

            // Show results
            if (successCount > 0) {
                showSnackbar(
                    `Status saved successfully!`,
                    'success'
                );
                
                // Close dialog and clear selections after successful save
                setSelectedItems([]);
                closeAdequacyDialog();
            }

            if (errorCount > 0 && errors.length > 0) {
                console.error('Staff adequacy submission errors:', errors);
                showSnackbar('Some entries failed to save', 'error');
            }

            // Trigger refresh to reload DA-NC data
            setRefreshTrigger((prev) => prev + 1);

        } catch (error) {
            console.error('Error submitting staff adequacy check:', error);
            showSnackbar('Error submitting adequacy check: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    }, [adequacyStatus, adequacyRemarks, selectedItems, dancRecords, orgId, appId, currentUserId, isDropdownSelection, selectedClauses, assessmentId, assessorId]);

    // Bulk Adequacy Check Handlers
    const handleOpenBulkAdequacyModal = () => {
        if (selectedItems.length === 0) {
            showSnackbar(
                "Please select at least one staff member for adequacy check",
                "warning"
            );
            return;
        }
        setSelectedAdequacyStaff([...selectedItems]);
        setBulkAdequacyModalOpen(true);
        fetchBulkScrutinyRemarks();
    };

    const fetchBulkScrutinyRemarks = async () => {
        setBulkLoadingRemarks(true);
        try {
            const responseApi = await getScrutinyRemarks({
                sectionName: 'Staff',
                type: localStorage.getItem('userTypeId') === '6' ? 'Assessment' : 'Document Review'
            });
            const response = responseApi.data;
            if (response.success && response.data) {
                setBulkScrutinyRemarks(response.data);
            } else {
                setBulkScrutinyRemarks([]);
            }
        } catch (error) {
            console.error('Error fetching bulk scrutiny remarks:', error);
            setBulkScrutinyRemarks([]);
        } finally {
            setBulkLoadingRemarks(false);
        }
    };

    const handleCloseBulkAdequacyModal = () => {
        setBulkAdequacyModalOpen(false);
        setBulkAdequacyStatus("");
        setBulkAdequacyRemarks("");
        setBulkScrutinyRemarks([]);
        setSelectedClauses([]);
        setClauseSearchFilter("");
    };

    // Helper function to filter clauses based on search
    const getFilteredClauses = () => {
        if (!clauseSearchFilter) return clauseNo;
        const searchLower = clauseSearchFilter.toLowerCase();
        return clauseNo.filter(clause => {
            const clauseNoMatch = clause?.clauseNo?.toLowerCase() || '';
            const displayNameMatch = clause?.displayName?.toLowerCase() || '';
            const nameMatch = clause?.name?.toLowerCase() || '';
            return clauseNoMatch.includes(searchLower) || 
                   displayNameMatch.includes(searchLower) || 
                   nameMatch.includes(searchLower);
        });
    };

    // Helper function to filter clauses for discipline dialog
    const getDisciplineFilteredClauses = () => {
        if (!disciplineClauseSearchFilter) return clauseNo;
        const searchLower = disciplineClauseSearchFilter.toLowerCase();
        return clauseNo.filter(clause => {
            const clauseNoMatch = clause?.clauseNo?.toLowerCase() || '';
            const displayNameMatch = clause?.displayName?.toLowerCase() || '';
            const nameMatch = clause?.name?.toLowerCase() || '';
            return clauseNoMatch.includes(searchLower) || 
                   displayNameMatch.includes(searchLower) || 
                   nameMatch.includes(searchLower);
        });
    };

    const handleBulkAdequacySubmit = async () => {
        const userTypeId = localStorage.getItem('userTypeId');
        
        if (
            !bulkAdequacyStatus ||
            (bulkAdequacyStatus === "Inadequate" && !bulkAdequacyRemarks?.trim()) ||
            (bulkAdequacyStatus === "Inadequate" && userTypeId === '6' && selectedClauses.length === 0)
        ) {
            showSnackbar(
                userTypeId === '6' && bulkAdequacyStatus === "Inadequate" && selectedClauses.length === 0
                    ? "Please select at least one clause number for Assessment inadequate status"
                    : "Please fill all required fields",
                "error"
            );
            return;
        }

        setBulkAdequacyLoading(true);
        
        try {
            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            for (const staffId of selectedAdequacyStaff) {
                try {
                    const existingRecord = dancRecords.find(record => 
                        record.sourceid === staffId && record.typeid === assessmentId
                    );

                    const ncType = assessorId === "4" ? "DPR" : "Assessment";

                    if (existingRecord) {
                        // Update existing record
                        const updateData = {
                            informationStatus: bulkAdequacyStatus,
                            remarks: bulkAdequacyRemarks,
                            isactive: 1,
                        };

                        // Add ClauseNo for Assessment users (userType 6) when clauses are selected
                        if (userTypeId === '6' && selectedClauses.length > 0) {
                            // Create a comma-separated string of clause numbers
                            const clauseNumbers = selectedClauses.map(clause => 
                                clause?.ClauseNo || clause?.DisplayName || clause
                            ).join(', ');
                            updateData.ClauseNo = clauseNumbers;
                        }

                        const response = await updateDANCBySourceId(
                            orgId,
                            appId,
                            staffId,
                            "staff_info",
                            assessmentId,
                            updateData
                        );

                        if (response.success) {
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to update record');
                        }
                    } else {
                        // Create new record
                        const baseBulkDANCData = {
                            typeid: assessmentId,
                            sectionName: "staff_info",
                            informationStatus: bulkAdequacyStatus,
                            orgId: orgId,
                            AppId: appId,
                            remarks: bulkAdequacyStatus === "Inadequate" 
                                ? bulkAdequacyRemarks || "Document adequacy marked as Inadequate"
                                : "Document adequacy check completed",
                            sourceid: staffId,
                            Insertedby: currentUserId,
                            NCtype: ncType,
                        };

                        // Add ClauseNo for Assessment users (userType 6) when clauses are selected
                        if (userTypeId === '6' && selectedClauses.length > 0) {
                            // Create a comma-separated string of clause numbers
                            const clauseNumbers = selectedClauses.map(clause => 
                                clause?.ClauseNo || clause?.DisplayName || clause
                            ).join(', ');
                            baseBulkDANCData.ClauseNo = clauseNumbers;
                        }

                        const response = await insertDANC(baseBulkDANCData);

                        if (response.success) {
                            successCount++;
                        } else {
                            throw new Error(response.message || 'Failed to create record');
                        }
                    }
                } catch (error) {
                    console.error(`Error processing staff ${staffId}:`, error);
                    errors.push(`Staff ${staffId}: ${error.message}`);
                    errorCount++;
                }
            }

            // Update adequacy checks locally for all selected staff
            const updatedAdequacyChecks = { ...adequacyChecks };
            selectedAdequacyStaff.forEach((staffId) => {
                const adequacyUpdate = {
                    status: bulkAdequacyStatus,
                    remarks: bulkAdequacyRemarks,
                    isSubmitted: true,
                    submittedAt: new Date().toISOString(),
                };

                // Add clause number for Assessment users
                if (userTypeId === '6' && selectedClauses.length > 0) {
                    // Create a comma-separated string of clause numbers
                    const clauseNumbers = selectedClauses.map(clause => 
                        clause?.ClauseNo || clause?.DisplayName || clause
                    ).join(', ');
                    adequacyUpdate.clauseNo = clauseNumbers;
                }

                updatedAdequacyChecks[staffId] = adequacyUpdate;
            });
            setAdequacyChecks(updatedAdequacyChecks);

            // Show results
            if (successCount > 0) {
                showSnackbar(
                    `Bulk adequacy check completed: ${successCount} staff member(s) processed successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}`,
                    errorCount > 0 ? 'warning' : 'success'
                );
            }

            if (errorCount > 0 && errors.length > 0) {
                console.error('Bulk staff adequacy submission errors:', errors);
            }

            // Trigger refresh to reload data
            setRefreshTrigger((prev) => prev + 1);

            // Clear selections and close modal
            setSelectedItems([]);
            handleCloseBulkAdequacyModal();
        } catch (error) {
            console.error('Error submitting bulk staff adequacy check:', error);
            showSnackbar('Error submitting bulk adequacy check: ' + error.message, 'error');
        } finally {
            setBulkAdequacyLoading(false);
        }
    };

    // Memoized View Staff Tab Component
    const renderViewStaffTab = useCallback(
      () => (
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Lab Staff List
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                {selectedItems.length > 0 && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleMarkAdequate}
                    sx={{
                      px: 3,
                      py: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Mark Adequate ({selectedItems.length})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={fetchStaffList}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
                <CircularProgress />
              </Box>
            ) : staffList.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography variant="body1" color="text.secondary">
                  No staff members found. Add staff members from the "Add Staff"
                  tab.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  borderRadius: 2,
                  border: "1px solid #e0e0e0",
                }}
              >
                <Table
                  stickyHeader
                  sx={{
                    borderCollapse: "separate",
                    "& .MuiTableCell-root": {
                      borderRight: "1px solid #e0e0e0",
                      padding: "12px 16px",
                    },
                    "& .MuiTableCell-root:last-child": {
                      borderRight: "none",
                    },
                    "& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root":
                      {
                        borderColor: "#c5cae9",
                      },
                  }}
                >
                  <TableHead>
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      }}
                    >
                      {(applicationStage === "18" ||
                        applicationStage === "18b" ||
                        applicationStage === "20") &&
                        (localStorage.getItem("userTypeId") === "4" ||
                          localStorage.getItem("userTypeId") === "6") && (
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              color: "white",
                              borderBottom: "2px solid #5a67d8",
                              backgroundColor: "transparent",
                              borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                              width: "50px",
                            }}
                          >
                            <Checkbox
                              checked={
                                selectedItems.length === filteredData.length &&
                                filteredData.length > 0
                              }
                              indeterminate={
                                selectedItems.length > 0 &&
                                selectedItems.length < filteredData.length
                              }
                              onChange={handleSelectAll}
                              sx={{
                                color: "white",
                                "&.Mui-checked": {
                                  color: "white",
                                },
                                "&.MuiCheckbox-indeterminate": {
                                  color: "white",
                                },
                              }}
                            />
                          </TableCell>
                        )}
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderBottom: "2px solid #5a67d8",
                          backgroundColor: "transparent",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        S.No
                      </TableCell>
                      {(applicationStage === "18" ||
                        applicationStage === "18a" ||
                        applicationStage === "18b" ||
                        applicationStage === "20") && (
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "white",
                            textAlign: "center",
                            borderBottom: "2px solid #5a67d8",
                            backgroundColor: "transparent",
                            borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            Adequacy Status
                          </Box>
                        </TableCell>
                      )}
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderBottom: "2px solid #5a67d8",
                          backgroundColor: "transparent",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <BusinessIcon fontSize="small" />
                          Employee Type
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderBottom: "2px solid #5a67d8",
                          backgroundColor: "transparent",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        Person Details
                      </TableCell>

                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "white",
                          borderBottom: "2px solid #5a67d8",
                          backgroundColor: "transparent",
                          borderRight: "1px solid rgba(255, 255, 255, 0.2)",
                        }}
                      >
                        Professional Details
                      </TableCell>

                      {(applicationStage === null ||
                        applicationStage === "" ||
                        applicationStage === "18a" ||
                        isSectionUnfrozen("Lab Staff")) && (
                        <TableCell
                          sx={{
                            fontWeight: 600,
                            color: "white",
                            borderBottom: "2px solid #5a67d8",
                            backgroundColor: "transparent",
                          }}
                        >
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredData.map((staff, index) => (
                      <TableRow
                        key={staff.Id}
                        sx={{
                          "&:hover": {
                            backgroundColor: "#e8f4fd",
                            transition: "background-color 0.2s ease",
                          },
                          backgroundColor:
                            index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                          borderBottom: "1px solid #e0e0e0",
                          "&:last-child td": {
                            borderBottom: "none",
                          },
                        }}
                      >
                        {(applicationStage === "18" ||
                          applicationStage === "18b" ||
                          applicationStage === "20") &&
                          (localStorage.getItem("userTypeId") === "4" ||
                            localStorage.getItem("userTypeId") === "6") && (
                            <TableCell>
                              <Checkbox
                                checked={selectedItems.includes(staff.Id)}
                                onChange={() => handleSelectItem(staff.Id)}
                                color="primary"
                              />
                            </TableCell>
                          )}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {index + 1}
                          </Typography>
                        </TableCell>
                        {(applicationStage === "18" ||
                          applicationStage === "18a" ||
                          applicationStage === "18b" ||
                          applicationStage === "20") && (
                          <TableCell>
                            {(() => {
                              const status = getStaffStatus(staff.Id);
                              const orgStatuses =
                                staffOrgStatuses[staff.Id] || [];

                              return (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                  }}
                                >
                                  {/* Display DANC Status */}
                                  {status.status && (
                                    <Box
                                      sx={{
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 1,
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        textAlign: "center",
                                        minWidth: "80px",
                                        bgcolor:
                                          status.status === "Adequate"
                                            ? "#e8f5e8"
                                            : status.status === "Inadequate"
                                              ? "#ffebee"
                                              : "#f5f5f5",
                                        color:
                                          status.status === "Adequate"
                                            ? "#2e7d32"
                                            : status.status === "Inadequate"
                                              ? "#c62828"
                                              : "#666",
                                        border: `1px solid ${
                                          status.status === "Adequate"
                                            ? "#a5d6a7"
                                            : status.status === "Inadequate"
                                              ? "#ef9a9a"
                                              : "#ddd"
                                        }`,
                                      }}
                                    >
                                      {status.status || "Not Assessed"}
                                    </Box>
                                  )}

                                  {status.status === "Inadequate" &&
                                    status.remarks && (
                                      <Tooltip title={status.remarks} arrow>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "#e62300ff",
                                            fontStyle: "italic",
                                          }}
                                        >
                                          "{status.remarks}"
                                        </Typography>
                                      </Tooltip>
                                    )}

                                  {status.ClauseNo && (
                                    <Chip
                                      label={`Clause: ${
                                        typeof status.ClauseNo === "object"
                                          ? Array.isArray(status.ClauseNo)
                                            ? status.ClauseNo.map(
                                                (c) =>
                                                  c?.ClauseNo ||
                                                  c?.DisplayName ||
                                                  c?.Name ||
                                                  c,
                                              ).join(", ")
                                            : JSON.stringify(status.ClauseNo)
                                          : status.ClauseNo
                                      }`}
                                      size="small"
                                    />
                                  )}

                                  {/* Display Discipline-wise Org Statuses */}
                                  {orgStatuses.length > 0 && (
                                    <Box
                                      sx={{
                                        mt: 1,
                                        borderTop: "1px solid #e0e0e0",
                                        pt: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          fontWeight: 600,
                                          color: "#666",
                                          display: "block",
                                          mb: 0.5,
                                        }}
                                      >
                                        📋 Discipline-wise Status:
                                      </Typography>
                                      {orgStatuses.map((orgStatus, idx) => {
                                        const disciplineName =
                                          disciplines.find(
                                            (d) =>
                                              d.DisciplineId?.toString() ===
                                              orgStatus.disciplineid?.toString(),
                                          )?.DisciplineName ||
                                          orgStatus.disciplineid;
                                        return (
                                          <Box
                                            key={idx}
                                            sx={{
                                              mb: 0.5,
                                              p: 1,
                                              bgcolor: "#f9f9f9",
                                              borderRadius: 1,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                mb: 0.5,
                                              }}
                                            >
                                              <Typography
                                                variant="caption"
                                                sx={{ fontWeight: 600 }}
                                              >
                                                {disciplineName}:
                                              </Typography>
                                              <Chip
                                                label={orgStatus.status}
                                                size="small"
                                                color={
                                                  orgStatus.status ===
                                                  "Adequate"
                                                    ? "success"
                                                    : orgStatus.status ===
                                                        "Inadequate"
                                                      ? "error"
                                                      : "default"
                                                }
                                                sx={{
                                                  fontSize: "0.65rem",
                                                  height: 20,
                                                }}
                                              />
                                            </Box>
                                            {orgStatus.remarks && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#666",
                                                  fontStyle: "italic",
                                                  display: "block",
                                                }}
                                              >
                                                "{orgStatus.remarks}"
                                              </Typography>
                                            )}
                                            {orgStatus.status ===
                                              "Inadequate" &&
                                              orgStatus.clauseNo && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    color: "#1976d2",
                                                    fontWeight: 500,
                                                    display: "block",
                                                    mt: 0.5,
                                                  }}
                                                >
                                                  📋 Clause:{" "}
                                                  {orgStatus.clauseNo}
                                                </Typography>
                                              )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )}

                                  {/* Mark Adequate Button */}
                                  {(applicationStage === "18" ||
                                    applicationStage === "18b" ||
                                    applicationStage === "20") &&
                                    (localStorage.getItem("userTypeId") ===
                                      "4" ||
                                      localStorage.getItem("userTypeId") ===
                                        "6") &&
                                    staff.IsAuthorisedSignatory && (
                                      <Button
                                        size="small"
                                        variant="contained"
                                        color="success"
                                        onClick={() =>
                                          handleMarkAdequateRow(staff.Id)
                                        }
                                        sx={{
                                          mt: 0.5,
                                          textTransform: "none",
                                          fontSize: "0.7rem",
                                          py: 0.5,
                                          px: 1,
                                        }}
                                        startIcon={
                                          <CheckIcon fontSize="small" />
                                        }
                                      >
                                        Mark Adequate
                                      </Button>
                                    )}
                                </Box>
                              );
                            })()}
                          </TableCell>
                        )}
                        <TableCell>
                          <Chip
                            label={staff.EmployeeType}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{
                              maxWidth: "100%", // allow wrapping within container
                              height: "auto", // allow height to grow
                              alignItems: "flex-start",
                              "& .MuiChip-label": {
                                whiteSpace: "normal", // ✅ allows text wrapping
                                wordBreak: "break-word",
                                lineHeight: 1.3,
                                paddingY: "4px",
                                textAlign: "left",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.6,
                            }}
                          >
                            <Typography fontWeight={600}>
                              {staff.Name}
                            </Typography>

                            <Typography variant="body2">
                              <strong>Designation:</strong> {staff.Designation}
                            </Typography>

                            <Typography variant="body2">
                              <strong>Qualification:</strong>{" "}
                              {staff.Qualification}
                            </Typography>

                            <Typography variant="body2">
                              <strong>Specialization:</strong>{" "}
                              {staff.Specialization}
                            </Typography>

                            <Typography variant="body2">
                              <strong>Experience:</strong>{" "}
                              {staff.RelevantExperience} years
                            </Typography>

                            <Typography variant="body2" noWrap>
                              <strong>Training:</strong>{" "}
                              {staff.RelevantTraining}
                            </Typography>

                            <Typography variant="body2">
                              📞 {staff.ContactNo}
                            </Typography>

                            <Typography variant="body2">
                              ☎️ {staff.FaxNo || "-"}
                            </Typography>

                            <Typography variant="body2">
                              ✉️ {staff.EmailID}
                            </Typography>
                          </Box>
                        </TableCell>

                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 0.8,
                            }}
                          >
                            {/* Discipline */}
                            <Box>
                              <Typography variant="caption">
                                Discipline - 
                              </Typography>
                              {staff.DisciplineNames ? (
                                <Chip
                                  size="small"
                                  label={staff.DisciplineNames}
                                />
                              ) : (
                                "-"
                              )}
                            </Box>
                            {/* Group */}
                            <Box>
                              <Typography variant="caption">
                                Group - 
                              </Typography>
                              {staff.GroupNames ? (
                                <Chip
                                  size="small"
                                  label={staff.GroupNames}
                                />
                              ) : (
                                "-"
                              )}
                            </Box>

                            {/* Authorization */}
                            <Box>
                              <Typography variant="caption">
                                Authorization - 
                              </Typography>
                              <Chip
                                size="small"
                                color={
                                  staff.IsAuthorisedSignatory
                                    ? "success"
                                    : "error"
                                }
                                label={
                                  staff.IsAuthorisedSignatory
                                    ? "Authorized"
                                    : "Not Authorized"
                                }
                              />
                            </Box>

                            {/* Specimen Signature */}
                            <Box>
                              <Typography variant="caption">
                                Signature
                              </Typography>
                              {staff.SignatorySign ? (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    s3Utils.handleFileViewWithS3(
                                      staff.SignatorySign,
                                    )
                                  }
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              ) : (
                                " No File"
                              )}
                            </Box>
                            {/* Photo */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="caption">
                                Photo -{" "}
                              </Typography>
                              {staff.photo ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    component="img"
                                    src={staff.photo}
                                    alt="Staff Photo"
                                    sx={{
                                      width: 60,
                                      height: 60,
                                      borderRadius: 1,
                                      objectFit: "cover",
                                    }}
                                  />

                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      s3Utils.handleFileViewWithS3(staff.photo)
                                    }
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              ) : (
                                " No File"
                              )}
                            </Box>

                            {/* Engagement */}
                            <Box>
                              <Typography variant="caption">
                                Engagement
                              </Typography>
                              <Typography variant="body2">
                                {staff.typeofEngagement || "-"}
                              </Typography>
                            </Box>

                            {/* Timing */}
                            <Box>
                              <Typography variant="caption">Timing</Typography>
                              <Typography variant="body2">
                                {staff.Timming || "-"}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {(applicationStage === null ||
                          applicationStage === "" ||
                          applicationStage === "18a" ||
                          isSectionUnfrozen("Lab Staff")) && (
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Edit Staff" arrow>
                                <IconButton
                                  onClick={() => handleEditStaff(staff)}
                                  size="small"
                                  sx={{
                                    color: "#1976d2",
                                    backgroundColor: "#e3f2fd",
                                    "&:hover": {
                                      backgroundColor: "#bbdefb",
                                      color: "#0d47a1",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Staff" arrow>
                                <IconButton
                                  onClick={() => handleDeleteStaff(staff.Id)}
                                  size="small"
                                  sx={{
                                    color: "#d32f2f",
                                    backgroundColor: "#ffebee",
                                    "&:hover": {
                                      backgroundColor: "#ffcdd2",
                                      color: "#b71c1c",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      ),
      [
        error,
        loading,
        staffList,
        fetchStaffList,
        selectedItems,
        handleSelectItem,
        handleSelectAll,
        handleMarkAdequate,
        handleMarkAdequateRow,
        handleOpenBulkAdequacyModal,
        getStaffStatus,
        adequacyChecks,
        applicationStage,
      ],
    );

    return (
      <>
        <Box p={3}>
          <Box
            sx={{
              py: 2,
              px: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              borderRadius: 1,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                🏢 Lab Staff
              </Typography>

              {/* Unfrozen section indicator */}
              {isSectionUnfrozen("Lab Staff") && (
                <Chip
                  label="🔓 UNFROZEN"
                  size="small"
                  sx={{
                    bgcolor: "#ff9800",
                    color: "white",
                    fontWeight: "bold",
                    animation: "pulse 2s infinite",
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Unfrozen Sections Notification */}
          {isSectionUnfrozen("Lab Staff") && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                🔓 Sections Unfrozen for Editing
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                The Lab Staff section has been temporarily unfrozen for editing.
                You can now add, modify, and delete staff records.
              </Typography>
              <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Chip
                  label="Lab Staff"
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontWeight: "bold" }}
                />
              </Box>
            </Alert>
          )}

          {/* Tab Navigation */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              sx={{
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              {(applicationStage === null ||
                applicationStage === "" ||
                applicationStage === "18a" ||
                applicationStage === "18" ||
                applicationStage === "18b" ||
                isSectionUnfrozen("Lab Staff")) && (
                <Tab
                  label={editingStaff ? "Edit Staff" : "Add Staff"}
                  sx={{ fontWeight: 600 }}
                />
              )}
              <Tab label="View Staff" sx={{ fontWeight: 600 }} />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          {(applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isSectionUnfrozen("Lab Staff")) &&
            tabValue === 0 &&
            renderAddStaffTab()}

          {tabValue ===
            (applicationStage === null ||
            applicationStage === "" ||
            applicationStage === "18a" ||
            applicationStage === "18" ||
            applicationStage === "18b" ||
            isSectionUnfrozen("Lab Staff")
              ? 1
              : 0) && renderViewStaffTab()}
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Adequacy Check Dialog */}
        <Dialog
          open={adequacyDialogOpen}
          onClose={closeAdequacyDialog}
          maxWidth="sm"
          fullWidth
          aria-labelledby="adequacy-dialog-title"
        >
          <DialogTitle
            id="adequacy-dialog-title"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              fontWeight: 600,
            }}
          >
            ✅ Mark Staff Adequacy Status
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Selected Staff Members: <strong>{selectedItems.length}</strong>
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="adequacy-status-label">
                    Adequacy Status
                  </InputLabel>
                  <Select
                    labelId="adequacy-status-label"
                    value={adequacyStatus}
                    label="Adequacy Status"
                    onChange={(e) => setAdequacyStatus(e.target.value)}
                  >
                    <MenuItem value="Adequate">Adequate</MenuItem>
                    <MenuItem value="Inadequate">Inadequate</MenuItem>
                    {userTypeId === "4" && (
                      <MenuItem value="To be Verify">To be Verify</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>
              {adequacyStatus === "Inadequate" &&
                localStorage.getItem("userTypeId") === "6" && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "white",
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        📋 Select Clause Numbers (Multiple Selection Allowed)
                      </Typography>

                      {/* Quick action buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const filteredClauses = getFilteredClauses();
                            setSelectedClauses((prev) => {
                              const newSelection = [...prev];
                              filteredClauses.forEach((clause) => {
                                const cKey = `${clause?.id || ""}-${
                                  clause?.name || clause?.displayName || ""
                                }`;
                                const exists = newSelection.some((c) => {
                                  const existingKey = `${c?.id || ""}-${
                                    c?.name || c?.displayName || ""
                                  }`;
                                  return existingKey === cKey;
                                });
                                if (!exists) {
                                  newSelection.push(clause);
                                }
                              });
                              // Update remarks
                              const clauseNames = newSelection
                                .map(
                                  (c) =>
                                    c?.name ||
                                    c?.displayName ||
                                    c?.clauseNo ||
                                    c,
                                )
                                .join(", ");
                              setAdequacyRemarks(clauseNames);
                              return newSelection;
                            });
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Select All {clauseSearchFilter ? "Visible" : ""}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setSelectedClauses([]);
                            setAdequacyRemarks("");
                          }}
                          disabled={selectedClauses.length === 0}
                          sx={{ textTransform: "none" }}
                        >
                          Clear All
                        </Button>
                      </Box>

                      {/* Search bar for clauses */}
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search clause numbers or names..."
                        value={clauseSearchFilter}
                        onChange={(e) => setClauseSearchFilter(e.target.value)}
                        inputRef={clauseSearchInputRef}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f8f9fa",
                            "& input": {
                              py: 1,
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <Box sx={{ mr: 1, color: "text.secondary" }}>
                              🔍
                            </Box>
                          ),
                          endAdornment: clauseSearchFilter && (
                            <Box
                              onClick={() => {
                                setClauseSearchFilter("");
                                clauseSearchInputRef.current?.focus();
                              }}
                              sx={{
                                cursor: "pointer",
                                color: "text.secondary",
                                "&:hover": { color: "error.main" },
                              }}
                            >
                              ✕
                            </Box>
                          ),
                        }}
                      />

                      <FormGroup row sx={{ maxHeight: 200, overflowY: "auto" }}>
                        {getFilteredClauses().map((clause, index) => (
                          <FormControlLabel
                            key={`clause-${clause?.id || index}-${
                              clause?.clauseNo || ""
                            }`}
                            control={
                              <Checkbox
                                checked={selectedClauses.some((c) => {
                                  // Use id and name combination for unique identification
                                  const cKey = `${c?.id || ""}-${
                                    c?.name || c?.displayName || ""
                                  }`;
                                  const clauseKey = `${clause?.id || ""}-${
                                    clause?.name || clause?.displayName || ""
                                  }`;
                                  return cKey === clauseKey;
                                })}
                                onChange={(e) =>
                                  handleClauseCheckboxChange(
                                    clause,
                                    e.target.checked,
                                  )
                                }
                                color="primary"
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {clause?.clauseNo ||
                                    clause?.displayName ||
                                    clause}
                                </Typography>
                                {clause?.name && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {clause.name}
                                  </Typography>
                                )}
                                {clause?.displayName &&
                                  clause?.clauseNo &&
                                  clause?.displayName !== clause?.name && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "text.secondary" }}
                                    >
                                      {clause.displayName}
                                    </Typography>
                                  )}
                              </Box>
                            }
                            sx={{
                              m: 0.5,
                              minWidth: "200px",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        ))}
                      </FormGroup>

                      {/* Search results info */}
                      {clauseSearchFilter && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            mt: 1,
                            display: "block",
                          }}
                        >
                          {getFilteredClauses().length} clause(s) found for "
                          {clauseSearchFilter}"
                        </Typography>
                      )}

                      {/* No results message */}
                      {clauseSearchFilter &&
                        getFilteredClauses().length === 0 && (
                          <Box
                            sx={{
                              textAlign: "center",
                              py: 2,
                              color: "text.secondary",
                            }}
                          >
                            <Typography variant="body2">
                              🔍 No clauses found matching "{clauseSearchFilter}
                              "
                            </Typography>
                            <Typography variant="caption">
                              Try searching by clause number, name, or display
                              name
                            </Typography>
                          </Box>
                        )}

                      {selectedClauses.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "primary.main",
                            mt: 1,
                            display: "block",
                          }}
                        >
                          ✓ {selectedClauses.length} clause(s) selected
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

              {adequacyStatus === "Inadequate" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Remarks"
                      variant="outlined"
                      value={adequacyRemarks}
                      onChange={(e) => setAdequacyRemarks(e.target.value)}
                      placeholder="Selected clause names will appear here automatically, or you can add custom remarks..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: 2,
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(33,150,243,0.15)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 12px rgba(33,150,243,0.25)",
                          },
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6c757d",
                        display: "block",
                        mt: 1,
                        fontStyle: "italic",
                      }}
                    >
                      {localStorage.getItem("userTypeId") === "6"
                        ? "Select clauses above to auto-populate this field, or manually add your assessment remarks."
                        : "Please provide detailed remarks for inadequate status."}
                    </Typography>
                  </Grid>
                </>
              )}

              {adequacyStatus === "Adequate" && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography variant="body2">
                      ✅ Selected staff members will be marked as{" "}
                      <strong>Adequate</strong>.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1, justifyContent: "space-between" }}>
            <Button
              onClick={closeAdequacyDialog}
              color="error"
              variant="outlined"
            >
              Close
            </Button>
            <Button
              onClick={handleAdequacySubmit}
              color="success"
              variant="contained"
              disabled={
                loading ||
                !adequacyStatus ||
                (adequacyStatus === "Inadequate" && !adequacyRemarks?.trim())
              }
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                px: 3,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {loading ? "Saving..." : "Save Status"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Discipline-wise Adequacy Check Dialog */}
        <Dialog
          open={disciplineDialogOpen}
          onClose={closeDisciplineDialog}
          maxWidth="md"
          fullWidth
          aria-labelledby="discipline-adequacy-dialog-title"
        >
          <DialogTitle
            id="discipline-adequacy-dialog-title"
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              fontWeight: 600,
            }}
          >
            ✅ Mark Staff Adequacy Status (Discipline-wise)
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="body1"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Staff Member:{" "}
                <strong>
                  {selectedStaffForDiscipline &&
                    staffList.find((s) => s.Id === selectedStaffForDiscipline)
                      ?.Name}
                </strong>
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {/* Display existing org status records */}
              {disciplineOrgStatusRecords.length > 0 && (
                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      📋 Existing Discipline-wise Status:
                    </Typography>
                    <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>Discipline</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Status</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Remarks</strong>
                            </TableCell>
                            <TableCell>
                              <strong>Clause No</strong>
                            </TableCell>
                            <TableCell align="center">
                              <strong>Action</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {disciplineOrgStatusRecords.map((record, index) => {
                            const disciplineName =
                              disciplines.find(
                                (d) =>
                                  d.DisciplineId?.toString() ===
                                  record.disciplineid?.toString(),
                              )?.DisciplineName || record.disciplineid;
                            return (
                              <TableRow key={index}>
                                <TableCell>{disciplineName}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={record.status}
                                    size="small"
                                    color={
                                      record.status === "Adequate"
                                        ? "success"
                                        : record.status === "Inadequate"
                                          ? "error"
                                          : "default"
                                    }
                                  />
                                </TableCell>
                                <TableCell
                                  sx={{
                                    maxWidth: 200,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {record.remarks || "-"}
                                </TableCell>
                                <TableCell>{record.clauseNo || "-"}</TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={async () => {
                                      try {
                                        // Call delete API
                                        const deleteResult =
                                          await deleteOrgStatus(record.id);

                                        if (deleteResult.success) {
                                          // Remove from UI
                                          setDisciplineOrgStatusRecords(
                                            (prev) =>
                                              prev.filter(
                                                (_, i) => i !== index,
                                              ),
                                          );
                                          // Update staffOrgStatuses state
                                          if (selectedStaffForDiscipline) {
                                            setStaffOrgStatuses((prev) => ({
                                              ...prev,
                                              [selectedStaffForDiscipline]:
                                                prev[
                                                  selectedStaffForDiscipline
                                                ]?.filter(
                                                  (r) => r.id !== record.id,
                                                ) || [],
                                            }));
                                          }
                                          showSnackbar(
                                            "Status deleted successfully",
                                            "success",
                                          );
                                        } else {
                                          showSnackbar(
                                            "Failed to delete status",
                                            "error",
                                          );
                                        }
                                      } catch (error) {
                                        console.error(
                                          "Error deleting status:",
                                          error,
                                        );
                                        showSnackbar(
                                          "Error deleting status: " +
                                            error.message,
                                          "error",
                                        );
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Grid>
              )}

              {/* Add new discipline-wise status */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  {disciplineOrgStatusRecords.length > 0
                    ? "➕ Add New Discipline Status:"
                    : "📝 Mark Adequacy Status:"}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="discipline-row-label">Discipline</InputLabel>
                  <Select
                    labelId="discipline-row-label"
                    value={selectedDisciplineForRow}
                    label="Discipline"
                    onChange={(e) => {
                      const selectedDisciplineId = e.target.value;
                      setSelectedDisciplineForRow(selectedDisciplineId);

                      // Auto-select the corresponding group
                      const matchingDiscipline = disciplines.find(
                        (d) =>
                          d.DisciplineId?.toString() ===
                          selectedDisciplineId?.toString(),
                      );
                      if (matchingDiscipline && matchingDiscipline.GroupId) {
                        setSelectedGroupForRow(matchingDiscipline.GroupId);
                      } else {
                        setSelectedGroupForRow("");
                      }
                    }}
                  >
                    {uniqueDisciplineOptions.map((discipline) => (
                      <MenuItem
                        key={discipline.DisciplineId}
                        value={discipline.DisciplineId}
                      >
                        {discipline.DisciplineName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="group-row-label">Group</InputLabel>
                  <Select
                    labelId="group-row-label"
                    value={selectedGroupForRow}
                    label="Group"
                    onChange={(e) => setSelectedGroupForRow(e.target.value)}
                  >
                    {uniqueGroupOptions.map((discipline) => (
                      <MenuItem
                        key={discipline.GroupId}
                        value={discipline.GroupId}
                      >
                        {discipline.groupName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="discipline-adequacy-status-label">
                    Adequacy Status
                  </InputLabel>
                  <Select
                    labelId="discipline-adequacy-status-label"
                    value={disciplineAdequacyStatus}
                    label="Adequacy Status"
                    onChange={(e) =>
                      setDisciplineAdequacyStatus(e.target.value)
                    }
                  >
                    <MenuItem value="Adequate">Adequate</MenuItem>
                    <MenuItem value="Inadequate">Inadequate</MenuItem>
                    {userTypeId === "4" && (
                      <MenuItem value="To be Verify">To be Verify</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              {disciplineAdequacyStatus === "Inadequate" &&
                localStorage.getItem("userTypeId") === "6" && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "white",
                        "&:hover": {
                          boxShadow: "0 2px 8px rgba(0,123,255,0.15)",
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ mb: 2, fontWeight: 600 }}
                      >
                        📋 Select Clause Numbers (Multiple Selection Allowed)
                      </Typography>

                      {/* Quick action buttons */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 2,
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            const filteredClauses =
                              getDisciplineFilteredClauses();
                            setSelectedDisciplineClauses((prev) => {
                              const newSelection = [...prev];
                              filteredClauses.forEach((clause) => {
                                const cKey = `${clause?.id || ""}-${
                                  clause?.name || clause?.displayName || ""
                                }`;
                                const exists = newSelection.some((c) => {
                                  const existingKey = `${c?.id || ""}-${
                                    c?.name || c?.displayName || ""
                                  }`;
                                  return existingKey === cKey;
                                });
                                if (!exists) {
                                  newSelection.push(clause);
                                }
                              });
                              // Update remarks
                              const clauseNames = newSelection
                                .map(
                                  (c) =>
                                    c?.name ||
                                    c?.displayName ||
                                    c?.clauseNo ||
                                    c,
                                )
                                .join(", ");
                              setDisciplineAdequacyRemarks(clauseNames);
                              return newSelection;
                            });
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Select All{" "}
                          {disciplineClauseSearchFilter ? "Visible" : ""}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setSelectedDisciplineClauses([]);
                            setDisciplineAdequacyRemarks("");
                          }}
                          disabled={selectedDisciplineClauses.length === 0}
                          sx={{ textTransform: "none" }}
                        >
                          Clear All
                        </Button>
                      </Box>

                      {/* Search bar for clauses */}
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search clause numbers or names..."
                        value={disciplineClauseSearchFilter}
                        onChange={(e) =>
                          setDisciplineClauseSearchFilter(e.target.value)
                        }
                        inputRef={disciplineClauseSearchInputRef}
                        sx={{
                          mb: 2,
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "#f8f9fa",
                            "& input": {
                              py: 1,
                            },
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <Box sx={{ mr: 1, color: "text.secondary" }}>
                              🔍
                            </Box>
                          ),
                          endAdornment: disciplineClauseSearchFilter && (
                            <Box
                              onClick={() => {
                                setDisciplineClauseSearchFilter("");
                                disciplineClauseSearchInputRef.current?.focus();
                              }}
                              sx={{
                                cursor: "pointer",
                                color: "text.secondary",
                                "&:hover": { color: "error.main" },
                              }}
                            >
                              ✕
                            </Box>
                          ),
                        }}
                      />

                      <FormGroup row sx={{ maxHeight: 200, overflowY: "auto" }}>
                        {getDisciplineFilteredClauses().map((clause, index) => (
                          <FormControlLabel
                            key={`discipline-clause-${clause?.id || index}-${
                              clause?.clauseNo || ""
                            }`}
                            control={
                              <Checkbox
                                checked={selectedDisciplineClauses.some((c) => {
                                  const cKey = `${c?.id || ""}-${
                                    c?.name || c?.displayName || ""
                                  }`;
                                  const clauseKey = `${clause?.id || ""}-${
                                    clause?.name || clause?.displayName || ""
                                  }`;
                                  return cKey === clauseKey;
                                })}
                                onChange={(e) =>
                                  handleDisciplineClauseCheckboxChange(
                                    clause,
                                    e.target.checked,
                                  )
                                }
                                color="primary"
                              />
                            }
                            label={
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {clause?.clauseNo ||
                                    clause?.displayName ||
                                    clause}
                                </Typography>
                                {clause?.name && (
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "text.secondary" }}
                                  >
                                    {clause.name}
                                  </Typography>
                                )}
                                {clause?.displayName &&
                                  clause?.clauseNo &&
                                  clause?.displayName !== clause?.name && (
                                    <Typography
                                      variant="caption"
                                      sx={{ color: "text.secondary" }}
                                    >
                                      {clause.displayName}
                                    </Typography>
                                  )}
                              </Box>
                            }
                            sx={{
                              m: 0.5,
                              minWidth: "200px",
                              "& .MuiFormControlLabel-label": {
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        ))}
                      </FormGroup>

                      {/* Search results info */}
                      {disciplineClauseSearchFilter && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            mt: 1,
                            display: "block",
                          }}
                        >
                          {getDisciplineFilteredClauses().length} clause(s)
                          found for "{disciplineClauseSearchFilter}"
                        </Typography>
                      )}

                      {/* No results message */}
                      {disciplineClauseSearchFilter &&
                        getDisciplineFilteredClauses().length === 0 && (
                          <Box
                            sx={{
                              textAlign: "center",
                              py: 2,
                              color: "text.secondary",
                            }}
                          >
                            <Typography variant="body2">
                              🔍 No clauses found matching "
                              {disciplineClauseSearchFilter}"
                            </Typography>
                            <Typography variant="caption">
                              Try searching by clause number, name, or display
                              name
                            </Typography>
                          </Box>
                        )}

                      {selectedDisciplineClauses.length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "primary.main",
                            mt: 1,
                            display: "block",
                          }}
                        >
                          ✓ {selectedDisciplineClauses.length} clause(s)
                          selected
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

              {disciplineAdequacyStatus === "Inadequate" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Remarks"
                      variant="outlined"
                      value={disciplineAdequacyRemarks}
                      onChange={(e) =>
                        setDisciplineAdequacyRemarks(e.target.value)
                      }
                      placeholder="Selected clause names will appear here automatically, or you can add custom remarks..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          borderRadius: 2,
                          "&:hover": {
                            boxShadow: "0 2px 8px rgba(33,150,243,0.15)",
                          },
                          "&.Mui-focused": {
                            boxShadow: "0 4px 12px rgba(33,150,243,0.25)",
                          },
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6c757d",
                        display: "block",
                        mt: 1,
                        fontStyle: "italic",
                      }}
                    >
                      {localStorage.getItem("userTypeId") === "6"
                        ? "Select clauses above to auto-populate this field, or manually add your assessment remarks."
                        : "Please provide detailed remarks for inadequate status."}
                    </Typography>
                  </Grid>
                </>
              )}

              {disciplineAdequacyStatus === "Adequate" && (
                <Grid item xs={12}>
                  <Alert severity="success">
                    <Typography variant="body2">
                      ✅ This staff member will be marked as{" "}
                      <strong>Adequate</strong> for the selected discipline.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1, justifyContent: "space-between" }}>
            <Button
              onClick={closeDisciplineDialog}
              color="error"
              variant="outlined"
            >
              Close
            </Button>
            <Button
              onClick={handleDisciplineAdequacySubmit}
              color="success"
              variant="contained"
              disabled={
                loading ||
                !disciplineAdequacyStatus ||
                !selectedDisciplineForRow ||
                (disciplineAdequacyStatus === "Inadequate" &&
                  !disciplineAdequacyRemarks?.trim())
              }
              startIcon={loading ? <CircularProgress size={20} /> : null}
              sx={{
                px: 3,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              {loading
                ? "Saving..."
                : disciplineOrgStatusRecords.length > 0
                  ? "Add Another Discipline"
                  : "Save Status"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Hourly Attendance Check Dialog */}
        <HourlyAttendanceDialog
          open={attendanceCheckDialogOpen}
          onClose={() => setAttendanceCheckDialogOpen(false)}
          message={attendanceCheckMessage}
          onRecheck={checkHourlyAttendance}
        />

        {/* Location Warning Dialog */}
        <LocationWarningDialog
          open={locationWarningOpen}
          onClose={() => setLocationWarningOpen(false)}
          distanceMessage={distanceMessage}
        />
      </>
    );
};

export default LabStaffSection;
